import { Fragment, Suspense, lazy, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { maMiekkieFiltry, naglowekKreski, pozycjaKreski, pozycjaKreskiBezZdjec } from '@/lib/filtryMiekkie';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpDown, ChevronDown, Gauge, Heart, Info, List, Map as MapIcon, SlidersHorizontal, X } from 'lucide-react';
import { useFavorites } from '@/hooks/useFavorites';
import { zUlubionymi } from '@/lib/favorites';
import { useMojeWyszukiwanie } from '@/hooks/useMojeWyszukiwanie';
import { useStanWyszukiwania } from '@/hooks/useStanWyszukiwania';
import { useMojeFiltry } from '@/hooks/useMojeFiltry';
import { useLiczbaBezOplat, useLiczbaSekcji, useLiczbySerp, useSerpCount, useSerpPins, useSourcePostsInfinite } from '@/hooks/useSourcePosts';
import { useDelayedFlag } from '@/hooks/useDelayedFlag';
import { useDotyk, useIsDesktop } from '@/hooks/use-mobile';
import { Toaster as ToasterSonner, toast } from 'sonner';
import { MAX_OBSZAROW, dodajObszar } from '@/lib/areaSelection';
import { PRIMARY_ACTIVE_CITY } from '@/lib/geoScope';
import { zamiarEdycjiZProfilu, zamiarPaneluFiltrow } from '@/lib/serpUrl';
import { krokPowrotu, oznaczPowrotDoCzegoSzukam } from '@/lib/powrotDoProfilu';
import { CZAS_DO_PYTANIA_MS } from '@/hooks/useMojeFiltry';
import { uporzadkujPoluzowanie } from '@/lib/serpChips';
import { formatLiczby } from '@/lib/i18nNumber';
import { useAnimatedCount } from '@/hooks/useAnimatedCount';
import { ODSTEP_OD_DOLU } from '@/lib/mobileBottomInset';
import { useAreaBoundaries, useWszystkieDzielnice } from '@/hooks/useAreaBoundaries';
import type { SourcePostFilters, SerpPost } from '@/hooks/useSourcePosts';
import { PINS_LIMIT, SERP_PAGE_SIZE, flattenSerpPages, serpFilterKey } from '@/hooks/serpQuery';
import { SourcePostCard } from '@/components/search/SourcePostCard';
import { PustyWynik } from '@/components/search/PustyWynik';
import { SearchFilters, PriceControl, RoomsControl, AreaControl } from '@/components/search/SearchFilters';
import SearchAreaBar from '@/components/search/SearchAreaBar';
import NaglowekMobilny from '@/components/search/NaglowekMobilny';
import { ZakladkaMojichFiltrow } from '@/components/search/ZakladkaMojichFiltrow';
import { PastylkaMoichFiltrow, PoleZapisuMoichFiltrow } from '@/components/search/PanelMoichFiltrow';
import { DzwonekAlertow } from '@/components/search/DzwonekAlertow';
import { useOtworzUstawieniaAlertow } from '@/hooks/useOtworzUstawieniaAlertow';
import { PytanieOMaile } from '@/components/search/PytanieOMaile';
import { maCene, stanPanelu } from '@/lib/mojeFiltry';
import { WarsawMetroIcon } from '@/components/offer/WarsawMetroBadge';
import { IKONA_FILTRA_POI, IkonaOdZaraz, IkonaZwierzat } from '@/components/search/IkonyFiltrowOkolicy';
import { ChipFiltra, RzadFiltrow } from '@/components/search/RzadFiltrow';
import { StalaSzerokosc } from '@/components/search/StalaSzerokosc';
import { kluczMiejsca, ukladRzedu, wKolejnosciPanelu, type IdStalegoFiltra, type SekcjaFiltrow } from '@/lib/rzadFiltrow';
import { zapiszWyszukiwanie } from '@/lib/historiaWyszukiwan';
import { etykietaObszaru, type SearchArea } from '@/lib/areaSearch';
import { etykietaWyboruPokoi, etykietaWyboruPokoiKrotka } from '@/lib/pokoje';
import SearchAreaOverlay from '@/components/search/SearchAreaOverlay';
// Code-split the map: MapLibre GL waży ~1,26 MB i zablokowałby narysowanie listy, gdyby wszedł
// do paczki strony. Leniwe wczytanie daje listę od razu, a mapa dociąga się za zasłoną.
const MapView = lazy(() =>
  import('@/components/search/MapView').then((m) => ({ default: m.MapView })),
);
// Type-only import (erased at build) — doesn't pull MapView into the page chunk.
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { dociagnijMape, dociagnijWBezczynnosci } from '@/lib/map/dociagnijMape';
import { wyczyszczoneFiltry, wyczyscZCofnieciem, liczbaElementowLokalizacji, bezLokalizacji, lokalizacjaZ, zLokalizacja, paramsToWidokMapy } from '@/lib/serpUrl';
import { etykietaCenyZ, formatRange } from '@/lib/serpFormat';

/**
 * Trailing icon for a desktop quick-filter pill: a clear-X when the filter is set
 * (clears just that filter), the dropdown chevron otherwise. The X stops pointer/
 * click propagation so it doesn't also open the popover. Rendered as a role=button
 * span — it lives inside the trigger <button>, where a nested <button> is invalid.
 */
/**
 * Przełącznik „Oceny okolicy” (oceny POI na kartach) — WYŁĄCZONY 28.09 decyzją Piotra:
 * „wymaga jeszcze pracy”. Kod zostaje; włączenie = `true`. PreLaunchChecklist 5.7.
 */
const POKAZ_OCENY_OKOLICY = false;

/**
 * Własny Toaster wyszukiwarki: komunikat po × lokalizacji ma stać NAD pływającym przyciskiem
 * „Mapa / Lista" (dół, środek), a globalny Sonner na telefonie rysuje się właśnie tam. Przycisku
 * nie przesuwamy — to komunikat dostaje odstęp: margines przycisku + jego wysokość (48 px) + 12.
 */
const TOASTER_WYSZUKIWARKI = 'wyszukiwarka';
/**
 * Jak długo wisi komunikat „Wyczyszczono N filtrów” z „Cofnij”: najwyżej 5 s (Maciek 16.10, plan
 * §1.74) — czyszczenie jest rutyną, nie ma po co trzymać komunikatu dłużej. Kroki E2E z „Cofnij”
 * robią czyszczenie i dotknięcie w JEDNYM kroku, bo na telefonie osobne kroki trwają dłużej.
 */
const CZAS_KOMUNIKATU_Z_COFNIJ_MS = 5_000;

/** Opcje sortowania wyszukiwarki — jedna lista dla wyboru i dla stałej szerokości etykiety. */
const SORTOWANIA = ['newest', 'oldest', 'cheapest', 'expensive'] as const;


const SearchPage = () => {
  const { t, i18n } = useTranslation();
  const isDesktop = useIsDesktop();
  // Adres czytamy tu już tylko raz (startowy kadr mapy); synchronizacją zajmuje się useStanWyszukiwania.
  const [searchParams] = useSearchParams();
  // Filtry, kadr mapy, chip „Moje wyszukiwanie” i podstawienie profilu, zsynchronizowane
  // z adresem: useStanWyszukiwania (tam opis pętli „disco” z 1.10 i dlaczego kierunki są rozdzielone).
  // Profil czytamy zawsze (dla najemcy): służy też jako punkt odniesienia dla „Zapisz zmiany”.
  const { data: mojeWyszukiwanie } = useMojeWyszukiwanie();
  const { filters: filtryStanu, setFilters, widokMapy, setWidokMapy, zProfilu, odrzucProfil } = useStanWyszukiwania({
    isDesktop,
    mojeWyszukiwanie,
  });
  // „Ulubione” (A6): lista serduszek oglądającego — z konta (RLS `flat.favorites`, tylko własne)
  // albo z przeglądarki dla gościa, dokładnie jak serduszka na kartach. Dokładana tu, w jednym
  // miejscu, do filtrów i szkicu, więc lista, licznik, piny i histogram widzą ten sam zbiór.
  const { ids: idsUlubionych } = useFavorites();
  const filters = useMemo(() => zUlubionymi(filtryStanu, idsUlubionych), [filtryStanu, idsUlubionych]);
  const [szkicStanu, setDraftFilters] = useState<SourcePostFilters>(filters);
  const draftFilters = useMemo(() => zUlubionymi(szkicStanu, idsUlubionych), [szkicStanu, idsUlubionych]);
  // Najświeższe filtry dla zapisu historii przy zamknięciu nakładki (zamknięcie następuje po
  // serii zmian obszarów w tym samym renderze).
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const [sheetOpen, setSheetOpen] = useState(false);
  // Telefon: chip Cena / Pokoje / Metraż otwiera krótki arkusz jednej sekcji zamiast całego panelu.
  const [sekcjaFiltrow, setSekcjaFiltrow] = useState<'cena' | 'pokoje' | 'metraz' | null>(null);
  // Sheet-only queries: live result count for the apply button (qasa's
  // "Show N results") and the real price range for the slider — both follow
  // the DRAFT filters as the user tweaks them.
  // Arkusz jednej sekcji (chip na telefonie) też ma „Pokaż N ofert” ze szkicu — bez niego liczba stała w miejscu.
  const { data: draftCount } = useSerpCount(draftFilters, { enabled: sheetOpen || sekcjaFiltrow !== null });
  // „Pokaż N ofert” tweenuje jak licznik nad listą (plan §1.33, A11): liczba płynie, słowo po
  // niej („oferta/oferty/ofert”) od razu z wartości końcowej.
  const draftCountShown = useAnimatedCount(draftCount ?? null) ?? draftCount;
  /**
   * STOPKA PANELU „N z M ogłoszeń potwierdza wybrane cechy” (Piotr 29.09). Tylko przy filtrze
   * miękkim; szkic opóźniony o 300 ms, żeby każde dotknięcie pigułki nie pytało bazy. N i M liczone
   * z TEGO SAMEGO opóźnionego szkicu — inaczej przez chwilę stałyby obok siebie liczby z dwóch stanów.
   */
  const [szkicOpozniony, setSzkicOpozniony] = useState(draftFilters);
  useEffect(() => {
    const t = setTimeout(() => setSzkicOpozniony(draftFilters), 300);
    return () => clearTimeout(t);
  }, [draftFilters]);
  const miekkieWSzkicu = maMiekkieFiltry(szkicOpozniony);
  const { data: potwierdzoneWSzkicu } = useLiczbaSekcji(szkicOpozniony, 'potwierdzone', { enabled: sheetOpen && miekkieWSzkicu });
  const { data: razemWSzkicu } = useSerpCount(szkicOpozniony, { enabled: sheetOpen && miekkieWSzkicu });

  /**
   * SRCH-15: ile ofert zabrała reguła „bez informacji o opłatach". Pytamy tylko wtedy, gdy
   * reguła w ogóle działa — czyli w trybie kosztu Z ustawioną ceną.
   */
  const { data: bezOplat } = useLiczbaBezOplat(filters, {
    enabled:
      filters.costMode === 'total' &&
      (typeof filters.rentMin === 'number' || typeof filters.rentMax === 'number'),
  });
  const view = filters.view ?? 'list';
  // Data is paginated server-side (.range pages of SERP_PAGE_SIZE); map mode
  // gets its own lightweight pins query instead of full rows. Only the active
  // mode fetches; both caches share the view-agnostic serpFilterKey, so
  // toggling list↔map within staleTime never refetches.
  // Desktop renders the hybrid (list + map side by side), so BOTH fetch. Mobile
  // toggles, so only the active view fetches.
  const listaWlaczona = isDesktop || view !== 'map';
  const listQ = useSourcePostsInfinite(filters, { enabled: listaWlaczona });
  /**
   * PINY CZEKAJĄ NA PIERWSZĄ STRONĘ LISTY (krok N, decyzja Piotra 27.09: „pierwsza strona
   * listy i licznik przed pinami"). Na pulpicie oba zapytania szły dotąd równolegle i na
   * wolnym łączu dzieliły się pasmem — zapytanie o piny jest cięższe (do 5 000 punktów),
   * więc opóźniało to, co człowiek widzi najpierw. W widoku mapy na telefonie listy nie ma
   * i piny ruszają od razu, bo wtedy TO one są pierwszą treścią.
   */
  const pinsQ = useSerpPins(filters, {
    enabled: (isDesktop || view === 'map') && !(listaWlaczona && listQ.isLoading),
  });
  /**
   * GRANICE DZIELNIC Z WYPRZEDZENIEM, razem z pinami (30.09). Widok miasta rysuje koła dopiero
   * przy granicach, a zapytanie startowało w MapView dopiero po inicjalizacji mapy (po
   * maplibre-gl.js, 305 KB gzip) — na 3G start w 7,1 s i koniec 0,4–0,9 s PO pinach. Tu, w
   * głównym kawałku, rusza z tym samym warunkiem co piny; MapView bierze wynik z cache (ten sam
   * klucz). 18 dzielnic = 9 KB gzip.
   */
  useWszystkieDzielnice((isDesktop || view === 'map') && !(listaWlaczona && listQ.isLoading));
  // Outline of the selected scope (Warsaw / district / rejon) drawn on the map.
  const boundaries = useAreaBoundaries(filters.areas ?? []).data ?? null;
  // Selected metro-station chips → "M" markers + proximity rings on the map.
  const stations = useMemo(
    () =>
      (filters.areas ?? []).flatMap((a) =>
        a.kind === 'station' ? [{ name: a.name, lat: a.lat, lon: a.lon, radiusM: a.radiusM }] : [],
      ),
    [filters.areas],
  );

  // Map fitBounds target — the most recently added estate chip.
  const focusBounds = useMemo(() => {
    const geo = [...(filters.areas ?? [])].reverse().find((a) => a.kind === 'geo');
    return geo && geo.kind === 'geo'
      ? ([[geo.south, geo.west], [geo.north, geo.east]] as [[number, number], [number, number]])
      : null;
  }, [filters.areas]);

  // District names already in the area filter — highlighted in the map's
  // "select districts" mode.
  const selectedDistricts = useMemo(
    () => (filters.areas ?? []).flatMap((a) => (a.kind === 'district' ? [a.name] : [])),
    [filters.areas],
  );
  // Click a district polygon on the map → add/remove it from the top area filter
  // (mirrors SearchAreaBar's chip behaviour: clears the legacy `suburbs` field).
  // Mapa nie ma własnego miejsca na komunikat o limicie (lista „Dzielnice” ma), więc mówi toastem.
  // Stałe `id`: aktualizator stanu może się wykonać dwa razy (StrictMode) — jeden toast, nie dwa.
  const limitNaMapie = useCallback(
    () => toast(t('search.area.limit_reached', { count: MAX_OBSZAROW }), { id: 'limit-obszarow-mapa' }),
    [t],
  );
  const toggleDistrict = useCallback(
    (name: string) => {
      setFilters((prev) => {
        const areas = prev.areas ?? [];
        const exists = areas.some((a) => a.kind === 'district' && a.name === name);
        // Dodanie idzie przez te same reguły co pole i lista „Dzielnice” (areaSelection.ts): osiedle
        // zastępuje swoją dzielnicę, dzielnica pochłania osiedla, najwyżej 8 obszarów (19.10).
        const wynik = exists ? null : dodajObszar(areas, { kind: 'district' as const, name });
        if (wynik?.limitOsiagniety) {
          limitNaMapie();
          return prev;
        }
        const next = wynik ? wynik.areas : areas.filter((a) => !(a.kind === 'district' && a.name === name));
        return { ...prev, areas: next.length ? next : undefined, suburbs: undefined };
      });
    },
    [setFilters, limitNaMapie],
  );

  // To samo dla OSIEDLI (rejonów MSI) — od etapu 3 tryb wyboru obszaru na mapie schodzi na
  // poziom osiedla, gdy mapa jest przybliżona. Osobna lista i osobne przełączanie, bo to inny
  // rodzaj obszaru w filtrze (`kind: 'subdistrict'`), a nie inna nazwa tego samego.
  const selectedSubdistricts = useMemo(
    () => (filters.areas ?? []).flatMap((a) => (a.kind === 'subdistrict' ? [a.name] : [])),
    [filters.areas],
  );
  const toggleSubdistrict = useCallback(
    (name: string) => {
      setFilters((prev) => {
        const areas = prev.areas ?? [];
        const exists = areas.some((a) => a.kind === 'subdistrict' && a.name === name);
        // Dodanie idzie przez te same reguły co pole i lista „Dzielnice” (areaSelection.ts): osiedle
        // zastępuje swoją dzielnicę, dzielnica pochłania osiedla, najwyżej 8 obszarów (19.10).
        const wynik = exists ? null : dodajObszar(areas, { kind: 'subdistrict' as const, name });
        if (wynik?.limitOsiagniety) {
          limitNaMapie();
          return prev;
        }
        const next = wynik ? wynik.areas : areas.filter((a) => !(a.kind === 'subdistrict' && a.name === name));
        return { ...prev, areas: next.length ? next : undefined, suburbs: undefined };
      });
    },
    [setFilters, limitNaMapie],
  );

  // The infinite-query page type widens rows to a minimal { id } shape; the
  // runtime rows are SerpPost (see fetchSerpPage), so cast the flattened result.
  const posts = useMemo(() => flattenSerpPages(listQ.data?.pages) as SerpPost[], [listQ.data]);
  const totalCount = listQ.data?.pages[0]?.count ?? null;
  const pins = pinsQ.data?.pins ?? [];
  const filterKey = JSON.stringify(serpFilterKey(filters));
  // On desktop the list is the primary scroll (map is sticky alongside), so use
  // the list's loading/error there; mobile follows the active toggle view.
  const isLoading = view === 'map' && !isDesktop ? pinsQ.isLoading : listQ.isLoading;
  const error = view === 'map' && !isDesktop ? pinsQ.error : listQ.error;
  // A CHANGED filter (drawing an area, moving a slider) is not `isLoading` —
  // keepPreviousData keeps the previous result set on screen, on purpose, so the
  // list doesn't flash empty. The cost is that nothing on screen admits the
  // results are stale: the old, unfiltered list just sits there, and the wait
  // reads as "nothing happened" rather than "working", so people redraw the area.
  // Both queries count, because desktop runs the list and the pins in parallel.
  // Paging is excluded — appending page N has its own "loading more" line and
  // must not dim cards the user is reading.
  // Delayed, so a fast refetch (the common case now: ~0.2 s) never flashes.
  const refreshing = useDelayedFlag(
    (listQ.isFetching && !listQ.isLoading && !listQ.isFetchingNextPage) ||
      // Dociąganie KOLEJNYCH stron pinów nie jest „odświeżaniem": mapa ma wtedy komplet z
      // pierwszej strony i dokłada resztę w tle. Bez tego wyjątku lista byłaby przygaszona
      // przez cały czas ładowania czterech stron (odkryte przy stronicowaniu pinów 27.09).
      (pinsQ.isFetching && !pinsQ.isLoading && !pinsQ.isFetchingNextPage),
  );

  // Render only a growing slice — mounting all ~300 image-heavy cards at once OOMs
  // iOS WebKit ("page reloads / can't be opened"). Grow by 24 as a sentinel near
  // the bottom scrolls into view.
  const PAGE = 24;
  /** Tyle pierwszych kart ładuje zdjęcie od razu — tyle mniej więcej widać na starcie. */
  const PILNE_ZDJECIA = 4;
  const [visibleCount, setVisibleCount] = useState(PAGE);
  const [showPoiBadges, setShowPoiBadges] = useState(false);
  // Hover-sync (qasa/airbnb/morizon): hovering a card highlights its map pin.
  // Stable callback so memoised cards don't re-render when the hovered id changes.
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const handleCardHover = useCallback((id: string | null) => setHoveredId(id), []);
  // The sticky search header floats just below the global header (nav + optional
  // banner) — its height comes from the --app-header-h CSS var the Header publishes,
  // so adding/removing the banner never breaks the offset. It stays on top while
  // scrolling. Measure ITS OWN height so the desktop sticky map
  // sits flush beneath it without a magic offset (the header grows/shrinks as the
  // chip row / count row appear).
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerH, setHeaderH] = useState(0);
  /**
   * Animacja wysokości/położenia kolumny mapy dopiero PO pierwszym namalowaniu zmierzonej
   * wartości. Bez tego kolumna rodziła się z `headerH = 0` (za wysoka o cały nagłówek) i przez
   * 200 ms kurczyła się przejściem CSS; MapLibre trzyma przy tym środek, więc treść mapy jechała
   * o połowę różnicy — zmierzone 29.09 po powrocie z oferty: 807 → 672 px, znaczniki o 68 px.
   */
  const [animujKolumneMapy, setAnimujKolumneMapy] = useState(false);
  useEffect(() => {
    if (animujKolumneMapy || headerH === 0) return;
    const r = requestAnimationFrame(() => requestAnimationFrame(() => setAnimujKolumneMapy(true)));
    return () => cancelAnimationFrame(r);
  }, [headerH, animujKolumneMapy]);
  // Blok jest „przyklejony", gdy lista pod nim jest przewinięta — wtedy dostaje cień, żeby
  // było widać, że karty przesuwają się POD nim, a nie że się urwały.
  const [przyklejony, setPrzyklejony] = useState(false);
  useEffect(() => {
    const naPrzewiniecie = () => setPrzyklejony(window.scrollY > 0);
    naPrzewiniecie();
    window.addEventListener('scroll', naPrzewiniecie, { passive: true });
    return () => window.removeEventListener('scroll', naPrzewiniecie);
  }, []);
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const measure = () => setHeaderH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // The sentinel lives in state (callback ref), not a ref: the list block —
  // sentinel included — unmounts on a map toggle, and an effect keyed on a
  // plain ref would keep observing the detached node forever, freezing the
  // infinite scroll after map→list.
  const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null);
  const total = posts.length;
  // Keyed on the FILTERS (not the posts array — page appends must not reset
  // scroll depth) + view, so returning from the map restarts at 24 cards
  // instead of re-mounting hundreds of image cards in one commit (iOS OOM).
  useEffect(() => { setVisibleCount(PAGE); }, [filterKey, view]);
  /**
   * POWRÓT ZE STRONY OFERTY WRACA NA TĘ SAMĄ POZYCJĘ LISTY.
   *
   * Zmierzone 27.09: po przewinięciu do 168 kart (y = 17 695 px), wejściu w ofertę i
   * cofnięciu lista wracała na sam początek, z 24 kartami. Człowiek, który przejrzał sto
   * kilkadziesiąt ofert i zajrzał w jedną, tracił całe przeglądanie — a to jest normalny
   * sposób korzystania z wyników, nie przypadek brzegowy.
   *
   * Zapisujemy KLUCZ FILTRÓW, liczbę odsłoniętych kart i pozycję przewinięcia; przywracamy
   * tylko wtedy, gdy filtry są te same (inaczej przywracalibyśmy pozycję z innego wyniku).
   * Strony trzyma w pamięci React Query, więc po powrocie wystarczy odsłonić tyle samo kart.
   * `sessionStorage` bywa niedostępne (tryb prywatny) — brak zapisu jest tu akceptowalny,
   * więc każdy dostęp ma własny `try`.
   */
  const KLUCZ_POZYCJI_LISTY = 'flh-serp-pozycja';
  const pozycjaPrzywrocona = useRef(false);
  // OSTATNIA ZNANA POZYCJA, a nie `window.scrollY` w chwili zapisu — i to jest poprawka do
  // pierwszej wersji, znaleziona pomiarem: przy wejściu w ofertę aplikacja przewija stronę na
  // górę ZANIM SearchPage zdąży się odmontować, więc zapisywaliśmy zero i „przywracaliśmy"
  // początek listy.
  const ostatniScrollY = useRef(0);
  useEffect(() => {
    const zapamietaj = () => {
      if (window.scrollY > 0) ostatniScrollY.current = Math.round(window.scrollY);
    };
    zapamietaj();
    window.addEventListener('scroll', zapamietaj, { passive: true });
    return () => window.removeEventListener('scroll', zapamietaj);
  }, []);
  useEffect(() => {
    const zapisz = () => {
      try {
        sessionStorage.setItem(
          KLUCZ_POZYCJI_LISTY,
          JSON.stringify({ filtry: filterKey, widoczne: visibleCount, y: ostatniScrollY.current }),
        );
      } catch { /* tryb prywatny — trudno */ }
    };
    window.addEventListener('pagehide', zapisz);
    return () => {
      window.removeEventListener('pagehide', zapisz);
      // Także przy odmontowaniu: wejście w ofertę to nawigacja wewnątrz aplikacji, bez
      // `pagehide`.
      zapisz();
    };
  }, [filterKey, visibleCount]);

  useEffect(() => {
    if (pozycjaPrzywrocona.current) return;
    let zapis: { filtry?: string; widoczne?: number; y?: number } | null = null;
    try {
      zapis = JSON.parse(sessionStorage.getItem(KLUCZ_POZYCJI_LISTY) ?? 'null');
    } catch { /* jw. */ }
    if (!zapis || zapis.filtry !== filterKey || !zapis.y) return;
    pozycjaPrzywrocona.current = true;
    if (zapis.widoczne) setVisibleCount((c) => Math.max(c, zapis.widoczne!));
    // Karty muszą się najpierw domalować — czekamy na WYSOKOŚĆ strony, a nie na stałą liczbę
    // milisekund. Osiem prób co 80 ms wystarcza; dalej i tak nie ma na co czekać.
    let proby = 0;
    const doScrolluj = () => {
      proby += 1;
      if (document.body.scrollHeight >= (zapis!.y ?? 0) + window.innerHeight * 0.5 || proby >= 8) {
        window.scrollTo(0, zapis!.y ?? 0);
        return;
      }
      setTimeout(doScrolluj, 80);
    };
    setTimeout(doScrolluj, 80);
  }, [filterKey]);

  // Mobile: switching to the map must reveal its TOP edge — otherwise the on-map
  // controls (draw / pick-districts / zoom, all top-aligned) sit above the fold
  // and the user has to scroll up to reach them. Scroll the page to the top so the
  // map's top (below the sticky header) is in view. Desktop is the hybrid (always
  // visible), so no-op there.
  useEffect(() => {
    if (view === 'map' && !isDesktop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [view, isDesktop]);
  // Telefon w liście: kawałki mapy w bezczynności, ale TYLKO na szybkim łączu bez oszczędzania
  // danych (`moznaDociagacWTle`). Na pulpicie mapa jest na ekranie od początku.
  const listaNarysowana = !isDesktop && view !== 'map' && !listQ.isLoading;
  useEffect(() => {
    if (!listaNarysowana) return;
    return dociagnijWBezczynnosci();
  }, [listaNarysowana]);
  useEffect(() => {
    if (!sentinelEl) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) setVisibleCount((c) => Math.min(c + PAGE, total)); },
      { rootMargin: '600px' },
    );
    io.observe(sentinelEl);
    return () => io.disconnect();
  }, [sentinelEl, total, visibleCount]);
  // Keep the fetch one page ahead of the render slice, so the sentinel never
  // waits on the network for rows that could already be in memory.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = listQ;
  useEffect(() => {
    if (view !== 'map' && total - visibleCount <= SERP_PAGE_SIZE && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [view, total, visibleCount, hasNextPage, isFetchingNextPage, fetchNextPage]);

  /**
   * SPRZĘŻENIE MAPA → LISTA (pulpit, etap F): kliknięcie pigułki przewija listę do tej karty.
   *
   * Karta bywa poza ZAŁADOWANYMI stronami i poza WYRENDEROWANYM wycinkiem — to dwie różne
   * granice i obie trzeba przesunąć. Strony doładowujemy po kolei (`useInfiniteQuery` nie umie
   * skoczyć do strony N — i tak wszystkie renderują się razem), a wycinek podnosimy do indeksu
   * znalezionej oferty.
   *
   * SUFIT PĘTLI ZALEŻY OD SORTOWANIA i to jest wniosek z pomiaru, nie ostrożność. Piny
   * pobieramy zawsze po `posted_at` malejąco, więc przy sortowaniu domyślnym („najnowsze")
   * kolejność pinów i kolejność listy to JEDNO I TO SAMO: klikalna oferta leży najdalej na
   * stronie `PINS_LIMIT / SERP_PAGE_SIZE` i pętla ma sens aż tam. Przy każdym innym
   * sortowaniu związek znika — przy „najtańsze" droga oferta z mapy ląduje na końcu z czterech
   * tysięcy. Zmierzone: pętla przez 21 stron kończyła się wtedy po 3,7 s i tak otwarciem
   * oferty. Cztery strony dają szybką odpowiedź w częstym przypadku (oferta blisko góry),
   * a w rzadkim nie każą czekać na nic.
   *
   * Był tu przez chwilę sufit sześciu stron, wzięty z pomiaru płynności: przy 312 kartach
   * lista dawała 22 fps, przy 912 — 5 fps. Po wprowadzeniu `content-visibility` na kartach
   * (patrz `.karta-leniwa` w index.css) krzywa jest płaska: 59–63 fps dla 24, 168, 312, 504
   * i 912 kart. Sufit wrócił więc tam, gdzie wynika z danych, a nie z wydolności rysowania.
   *
   * BILET unieważnia poprzednie szukanie: dwa kliknięcia pod rząd inaczej ścigałyby się o
   * przewijanie, a wygrywałoby to, które akurat później doładowało stronę.
   */
  const MAKS_STRON_DOSZUKIWANIA =
    (filters.sort ?? 'newest') === 'newest' ? Math.ceil(PINS_LIMIT / SERP_PAGE_SIZE) : 4;
  const [szukanaOferta, setSzukanaOferta] = useState<string | null>(null);
  const [podswietlonaKarta, setPodswietlonaKarta] = useState<string | null>(null);
  const biletSzukania = useRef(0);
  const nawiguj = useNavigate();

  const przewinDoKarty = useCallback(
    async (id: string) => {
      const bilet = ++biletSzukania.current;
      const indeksW = (lista: SerpPost[]) => lista.findIndex((p) => p.id === id);
      let lista = flattenSerpPages(listQ.data?.pages) as SerpPost[];
      let i = indeksW(lista);
      let doladowane = 0;
      if (i < 0) setSzukanaOferta(id);
      while (i < 0 && listQ.hasNextPage && doladowane < MAKS_STRON_DOSZUKIWANIA) {
        const wynik = await listQ.fetchNextPage();
        if (bilet !== biletSzukania.current) return;
        doladowane += 1;
        lista = flattenSerpPages(wynik.data?.pages) as SerpPost[];
        i = indeksW(lista);
      }
      if (bilet !== biletSzukania.current) return;
      setSzukanaOferta(null);
      if (i < 0) {
        // NIE ZNALEŹLIŚMY jej w zasięgu pętli — i to nie jest przypadek brzegowy do
        // przemilczenia. Piny przychodzą posortowane po dacie, a lista sortowaniem wybranym
        // przez człowieka, więc pin z mapy potrafi leżeć w liście dowolnie daleko (przy
        // „najtańsze" droga oferta ląduje na końcu z czterech tysięcy). Doładowanie
        // wszystkiego to tysiące kart w drzewie; zamiast tego otwieramy tę ofertę wprost.
        // Cisza po kliknięciu byłaby najgorszym z wyjść.
        nawiguj(`/offer/${id}`);
        return;
      }
      setVisibleCount((c) => Math.max(c, i + 1));
      setPodswietlonaKarta(id);
      // Karta pojawia się dopiero po domalowaniu powiększonego wycinka, więc czekamy na NIĄ,
      // a nie na stałą liczbę milisekund. Dziesięć prób co 60 ms to ~0,6 s — dłużej niż
      // potrzeba nawet przy 900 kartach, a przy braku karty po prostu kończymy.
      for (let proba = 0; proba < 10; proba += 1) {
        await new Promise((r) => setTimeout(r, 60));
        if (bilet !== biletSzukania.current) return;
        const el = document.querySelector(`[data-oferta="${id}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
    },
    [listQ, MAKS_STRON_DOSZUKIWANIA, nawiguj],
  );

  /**
   * PULPIT: oferta z adresu → przewinięcie listy do jej karty i podświetlenie (etap H).
   * Ten sam efekt obsługuje świeże kliknięcie w pigułkę i powrót z oferty, bo w obu
   * przypadkach kończy się to wpisem `oferta=` w adresie. Raz na identyfikator.
   */
  const przewinietaZAdresu = useRef<string | null>(null);
  useEffect(() => {
    const id = widokMapy.oferta;
    if (!id || !isDesktop || posts.length === 0) return;
    if (przewinietaZAdresu.current === id) return;
    przewinietaZAdresu.current = id;
    void przewinDoKarty(id);
  }, [widokMapy.oferta, isDesktop, posts.length, przewinDoKarty]);

  // Podświetlenie gaśnie samo — jest sygnałem „to ta karta", a nie stanem zaznaczenia.
  useEffect(() => {
    if (!podswietlonaKarta) return;
    const t = setTimeout(() => setPodswietlonaKarta(null), 4000);
    return () => clearTimeout(t);
  }, [podswietlonaKarta]);

  /**
   * KADR Z ADRESU WYGRYWA Z DOPASOWANIEM — dopóki człowiek nie zmieni wyszukiwania.
   *
   * Zmierzone: samo pominięcie pierwszego dopasowania nie wystarcza, bo obrysy obszaru
   * dochodzą z opóźnieniem i odpalają je po raz drugi — przywrócony kadr uciekał wtedy o
   * 720 m na środek obrysu Muranowa. Kadr z adresu obowiązuje więc aż do zmiany filtrów;
   * wtedy człowiek prosi o nowy widok, a nie wraca do starego.
   */
  const [kameraStartowa, setKameraStartowa] = useState(() => paramsToWidokMapy(searchParams).kamera ?? null);
  const filtryPrzyKamerze = useRef(filterKey);
  useEffect(() => {
    if (filterKey === filtryPrzyKamerze.current) return;
    filtryPrzyKamerze.current = filterKey;
    setKameraStartowa(null);
  }, [filterKey]);

  /** Kadr z mapy → adres. Zaokrąglenie robi `filtersToParams`, tu tylko przekazanie. */
  const naKamere = useCallback((k: { lat: number; lng: number; zoom: number }) => {
    setWidokMapy((w) => (w.kamera && w.kamera.lat === k.lat && w.kamera.lng === k.lng && w.kamera.zoom === k.zoom ? w : { ...w, kamera: k }));
  }, [setWidokMapy]);
  const naOtwartaOferte = useCallback((id: string | null) => {
    setWidokMapy((w) => (w.oferta === id ? w : { ...w, oferta: id }));
  }, [setWidokMapy]);

  // Each chip knows how to clear ITSELF — the X removes that one filter; a
  // click on the chip body still opens the sheet for editing.
  const activeChips = useMemo(() => {
    // `pastylka` = ten filtr ma na pulpicie własną pastylkę w górnym pasku (cena,
    // pokoje, metraż). Taki chip renderuje się tylko poniżej xl, żeby nie pokazywać
    // tej samej rzeczy dwa razy, i nie liczy się do znacznika przy „Wszystkie filtry".
    const chips: Array<{
      label: string;
      clear: (f: SourcePostFilters) => SourcePostFilters;
      pastylka?: boolean;
      pozaPanelem?: boolean;
      /** Ikona przed napisem (znak metra przy „≤10 min do metra”). */
      ikona?: React.ReactNode;
      /** Filtr ze stałym miejscem w rzędzie (szybki albo sugestia) — rząd rysuje go sam. */
      id?: IdStalegoFiltra;
      /** Sekcja panelu, na której otwiera się panel po dotknięciu chipa (brak = góra panelu). */
      sekcja?: SekcjaFiltrow;
      /**
       * Filtr tak/nie (Maciek 19.10): nie ma czego zmieniać poza wyłączeniem, więc dotknięcie
       * CAŁEGO chipa działa jak × — zamiast otwierać panel. Chipy z wartością (cena, pokoje,
       * metraż, kaucja, typ budynku…) dalej otwierają swoje okienko lub sekcję panelu.
       */
      przelacznik?: boolean;
    }> = [];
    if (filters.rentMin || filters.rentMax) {
      chips.push({
        // Chip jedzie w rzędzie, który się przewija — tam dopisek mieści się w jednej
        // linii i nie ma po co go łamać.
        label: [etykietaCenyZ(filters, t, i18n.language)?.zakres, etykietaCenyZ(filters, t, i18n.language)?.dopisek].filter(Boolean).join(' '),
        clear: (f) => ({ ...f, rentMin: undefined, rentMax: undefined, costMode: undefined }),
        pastylka: true,
        id: 'cena',
        sekcja: 'cena',
      });
    }
    if (filters.rooms?.length) {
      chips.push({
        // Jedna funkcja etykiety dla chipa, pastylki na pulpicie i pastylki na telefonie.
        label: etykietaWyboruPokoi(filters.rooms, t) ?? '',
        clear: (f) => ({ ...f, rooms: undefined }),
        pastylka: true,
        id: 'pokoje',
        sekcja: 'pokoje',
      });
    }
    if (filters.areaMin || filters.areaMax) {
      chips.push({
        label: `${formatRange(t, filters.areaMin, filters.areaMax, 'm²', i18n.language)}`,
        clear: (f) => ({ ...f, areaMin: undefined, areaMax: undefined }),
        pastylka: true,
        id: 'metraz',
        sekcja: 'metraz',
      });
    }
    if (filters.nearMetro) {
      chips.push({
        label: t('search.filters.near_metro'),
        ikona: <WarsawMetroIcon size={13} />,
        clear: (f) => ({ ...f, nearMetro: undefined }), przelacznik: true,
        id: 'metro',
        sekcja: 'okolica',
      });
    }
    for (const p of filters.poi ?? []) {
      const IkonaPoi = IKONA_FILTRA_POI[p as keyof typeof IKONA_FILTRA_POI];
      chips.push({
        label: t(`search.filters.poi_${p}`),
        ikona: IkonaPoi ? <IkonaPoi className="h-3 w-3" aria-hidden /> : undefined,
        sekcja: 'okolica',
        przelacznik: true,
        clear: (f) => {
          const next = (f.poi ?? []).filter((x) => x !== p);
          return { ...f, poi: next.length ? next : undefined };
        },
      });
    }
    // „Ze zwierzęciem" — jeden chip dla profilu i dla przełącznika w filtrach; widoczny i
    // usuwalny jak każdy inny filtr, bo odcina jawne „nie".
    if (filters.zeZwierzeciem) {
      chips.push({
        label: t('search.pets_chip'),
        ikona: <IkonaZwierzat size={13} />,
        clear: (f) => ({ ...f, zeZwierzeciem: undefined }), przelacznik: true,
        id: 'zwierzeta',
        sekcja: 'mieszkanie',
      });
    }
    // Filtry miękkie etapu 1b — chipy bez pastylki, więc liczą się do znacznika „Filtry".
    if (filters.umeblowanie) {
      chips.push({
        label: t(filters.umeblowanie === 'umeblowane' ? 'search.filters.furnished_yes' : 'search.filters.furnished_no'),
        clear: (f) => ({ ...f, umeblowanie: undefined }),
        sekcja: 'mieszkanie',
      });
    }
    if (filters.pietro) {
      chips.push({ label: t('search.filters.floor_ground'), clear: (f) => ({ ...f, pietro: undefined }), sekcja: 'mieszkanie', przelacznik: true });
    }
    if (filters.kuchnia) {
      chips.push({
        label: t(filters.kuchnia === 'oddzielna' ? 'search.filters.chip_kitchen_separate' : 'search.filters.chip_kitchen_annex'),
        clear: (f) => ({ ...f, kuchnia: undefined }),
        sekcja: 'mieszkanie',
      });
    }
    if (filters.lazienki) {
      chips.push({
        label: t(filters.lazienki === '1' ? 'search.filters.chip_baths_1' : 'search.filters.chip_baths_2plus'),
        clear: (f) => ({ ...f, lazienki: undefined }),
        sekcja: 'mieszkanie',
      });
    }
    if (filters.internet) {
      chips.push({ label: t('search.filters.chip_internet_fibre'), clear: (f) => ({ ...f, internet: undefined }), sekcja: 'mieszkanie', przelacznik: true });
    }
    if (filters.balkon) {
      chips.push({ label: t('search.filters.balcony'), clear: (f) => ({ ...f, balkon: undefined }), sekcja: 'mieszkanie', przelacznik: true });
    }
    if (filters.winda) {
      chips.push({ label: t('search.filters.elevator'), clear: (f) => ({ ...f, winda: undefined }), sekcja: 'mieszkanie', przelacznik: true });
    }
    if (filters.typBudynku) {
      chips.push({
        label: t(`search.filters.building_type_value.${filters.typBudynku}`),
        clear: (f) => ({ ...f, typBudynku: undefined }),
        sekcja: 'budynek',
      });
    }
    if (filters.wiekBudynku) {
      chips.push({
        label: t(`search.filters.building_age_value.${filters.wiekBudynku}`),
        clear: (f) => ({ ...f, wiekBudynku: undefined }),
        sekcja: 'budynek',
      });
    }
    for (const u of filters.udogodnienia ?? []) {
      chips.push({
        label: t(`search.filters.amenity.${u}`),
        sekcja: 'udogodnienia',
        przelacznik: true,
        clear: (f) => {
          const reszta = (f.udogodnienia ?? []).filter((x) => x !== u);
          return { ...f, udogodnienia: reszta.length ? reszta : undefined };
        },
      });
    }
    if (filters.kaucjaMax != null) {
      chips.push({
        label: t('search.filters.chip_deposit_max', { kwota: formatLiczby(filters.kaucjaMax, i18n.language) }),
        clear: (f) => ({ ...f, kaucjaMax: undefined }),
        sekcja: 'kaucja',
      });
    }
    // Pastylka „bez adresu” nad mapą ustawia ten filtr; chip go zdejmuje.
    if (filters.bezAdresu) {
      chips.push({
        label: t('search.filters.chip_no_address'),
        clear: (f) => ({ ...f, bezAdresu: undefined }), przelacznik: true,
      });
    }
    if (filters.wlascicielNaFlatHero) {
      chips.push({
        label: t('search.filters.owner_on_flathero'),
        clear: (f) => ({ ...f, wlascicielNaFlatHero: undefined }), przelacznik: true,
        sekcja: 'ogloszenie',
      });
    }
    if (filters.ulubione) {
      chips.push({
        label: t('search.filters.favourites'),
        ikona: <Heart className="h-3 w-3" aria-hidden />,
        clear: (f) => ({ ...f, ulubione: undefined, ulubioneIds: undefined }), przelacznik: true,
        sekcja: 'ogloszenie',
      });
    }
    if (filters.availableNow) {
      chips.push({
        label: t('search.filters.available_now'),
        ikona: <IkonaOdZaraz size={13} />,
        clear: (f) => ({ ...f, availableNow: undefined }), przelacznik: true,
        sekcja: 'ogloszenie',
      });
    }
    if (filters.postedSinceHours) {
      chips.push({
        label: t(`search.filters.posted_options.${filters.postedSinceHours}`),
        clear: (f) => ({ ...f, postedSinceHours: null }),
        sekcja: 'ogloszenie',
      });
    }
    // „Pokaż oferty bez zdjęć” — chip tylko przy stanie niedomyślnym (wyłączonym).
    if (filters.bezZdjec === false) {
      chips.push({
        label: t('search.filters.chip_only_with_photos'),
        clear: (f) => ({ ...f, bezZdjec: undefined }), przelacznik: true,
        sekcja: 'ogloszenie',
      });
    }
    if (filters.polygon && filters.polygon.length >= 3) {
      // `pozaPanelem`: narysowanego obszaru NIE MA w arkuszu filtrów — rysuje się go na
      // mapie i zdejmuje tym chipem. Znacznik przy „Filtry" mówi, ile filtrów trzyma panel,
      // więc doliczanie go pokazywało „1" i odsyłało do panelu, w którym nie ma czego
      // zmienić (ta sama reguła co SRCH-06).
      chips.push({
        label: t('search.draw.chip'),
        clear: (f) => ({ ...f, polygon: undefined }),
        pozaPanelem: true,
      });
    }
    // Reszta rzędu w kolejności panelu (plan E2.1 (1)); w sekcji — kolejność kontrolek wyżej.
    return wKolejnosciPanelu(chips);
  }, [filters, t, i18n.language]);

  // Filtry poza lokalizacją — zdanie „N filtrów" w nagłówku. Narysowany kształt ma swój chip
  // (`pozaPanelem`), ale jest lokalizacją, więc liczy go dopiero `doWyczyszczenia` niżej.
  const filterCount = activeChips.filter((c) => !c.pozaPanelem).length;
  // Ile zdejmie „Wyczyść": filtry + każdy element lokalizacji (plan §1.69). Bramkuje przyciski
  // „Wyczyść" i daje N w komunikacie „Wyczyszczono N filtrów". Sama lokalizacja też wystarcza.
  const doWyczyszczenia = filterCount + liczbaElementowLokalizacji(filters);


  /**
   * MOJE FILTRY (C1, plan E1.3): flaga i „Zapisz” w rzędzie filtrów — pulpit [Wszystkie | flaga |
   * Zapisz], telefon pigułka [flaga | Zapisz]. Zastępuje chip „Moje wyszukiwanie” z jego × i
   * „Zapisz”, okienko propozycji (pulpit), kartę propozycji (telefon) i „Zapisz” w oknie Filtry.
   */
  const mojeFiltry = useMojeFiltry({
    filters,
    moje: mojeWyszukiwanie,
    ustawFiltry: setFilters,
    toasterId: TOASTER_WYSZUKIWARKI,
    zablokowany: sheetOpen,
    // Wyłączenie flagi znaczy „nie chcę tego zestawu”, jak „Wyczyść”: start z profilu nie wraca w tej sesji.
    poWylaczeniu: () => { if (zProfilu) odrzucProfil(); },
  });
  /**
   * MOJE FILTRY W PANELU (C2, plan E1.4): pastylka w nagłówku i pole „Zapisz…” nad „Pokaż N ofert”.
   * Działają na szkicu; pole jest domyślnie odznaczone i zeruje się przy każdym otwarciu panelu,
   * dotknięciu pastylki i „Wyczyść” (makieta).
   */
  const [zapiszZPanelu, setZapiszZPanelu] = useState(false);
  const zbiorMoichFiltrow = mojeWyszukiwanie && mojeWyszukiwanie.savedAt !== null ? mojeWyszukiwanie.filters : null;
  const panelMoichFiltrow = stanPanelu({ zapisane: zbiorMoichFiltrow, szkic: draftFilters, zaznaczone: zapiszZPanelu });
  const naPastylke = () => {
    if (!zbiorMoichFiltrow) return;
    setZapiszZPanelu(false);
    setDraftFilters(panelMoichFiltrow.pigulkaZapalona ? wyczyszczoneFiltry(draftFilters) : { ...wyczyszczoneFiltry(draftFilters), ...zbiorMoichFiltrow });
  };
  /** Zatwierdzenie stanu: wpis historii. */
  const zatwierdz = (f: SourcePostFilters) => {
    zapiszWyszukiwanie(f);
  };

  /**
   * RZĄD FILTRÓW (propozycja Maćka, Piotr 6.10). Kolejność liczy `ukladRzedu` (src/lib/rzadFiltrow.ts):
   * Cena, Pokoje, Metraż (telefon: Metraż tylko, gdy ustawiony), sugestie Metro i Ze zwierzęciem
   * (w tym samym miejscu włączone i wyłączone), potem reszta aktywnych filtrów w kolejności panelu.
   * Pulpit: etykiety pełne, szybkie filtry otwierają swoje okienka; telefon: etykiety krótkie, Cena
   * i Pokoje otwierają mały arkusz. Dotknięcie innego chipa otwiera panel NA jego sekcji.
   */
  const elementyRzedu = (kompakt: boolean): React.ReactNode[] => {
    const lang = i18n.language;
    const cena = etykietaCenyZ(filters, t, lang);
    const chipZ = (id: IdStalegoFiltra) => activeChips.find((c) => c.id === id);
    const usun = (id: IdStalegoFiltra) => {
      const chip = chipZ(id);
      return chip ? () => setFilters(chip.clear(filters)) : undefined;
    };
    const aria = (nazwa: string) => t('search.filters.clear_one', { name: nazwa });
    const szybkie = {
      cena: {
        // Jak w makiecie i w podsumowaniu wiersza panelu: „do 4 800 zł z opłatami” (spacja, bez „·”).
        label: kompakt ? cena?.zakres : [cena?.zakres, cena?.dopisek].filter(Boolean).join(' '),
        nazwa: t('search.filters.price'),
        okienko: <PriceControl filters={filters} onChange={setFilters} />,
        sekcja: 'cena' as const,
      },
      pokoje: {
        label: kompakt
          ? (() => {
              const k = etykietaWyboruPokoiKrotka(filters.rooms, t);
              return k ? [k.tekst, k.znacznik].filter((x) => x != null).join(' ') : '';
            })()
          : etykietaWyboruPokoi(filters.rooms, t),
        nazwa: t('search.filters.rooms'),
        okienko: <RoomsControl filters={filters} onChange={setFilters} />,
        sekcja: 'pokoje' as const,
      },
      metraz: {
        label: formatRange(t, filters.areaMin, filters.areaMax, 'm²', lang),
        nazwa: t('search.filters.area'),
        okienko: <AreaControl filters={filters} onChange={setFilters} />,
        sekcja: 'metraz' as const,
      },
    };
    const sugestie = {
      metro: {
        label: t(kompakt ? 'search.filters.near_metro_short' : 'search.filters.near_metro'),
        ikona: <WarsawMetroIcon size={13} />,
        wlacz: { ...filters, nearMetro: true },
        sekcja: 'okolica' as const,
      },
      zwierzeta: {
        label: t('search.pets_chip'),
        ikona: <IkonaZwierzat size={13} />,
        wlacz: { ...filters, zeZwierzeciem: true },
        sekcja: 'mieszkanie' as const,
      },
    };
    return ukladRzedu(activeChips, kompakt).map((m) => {
      const klucz = kluczMiejsca(m, m.typ === 'inny' ? activeChips[m.indeks].label : undefined);
      if (m.typ === 'szybki') {
        const d = szybkie[m.id];
        const chip = (
          <ChipFiltra
            klucz={klucz}
            testId={`chip-${m.id}`}
            wariant={m.aktywny ? 'aktywny' : 'wylaczony'}
            kompakt={kompakt}
            label={m.aktywny ? d.label : d.nazwa}
            naUsun={usun(m.id)}
            ariaUsun={aria(d.nazwa)}
            onClick={kompakt ? () => {
              // Toast nigdy nie leży na arkuszu (§1.25) — jak przy `openSheet`.
              toast.dismiss(); setDraftFilters(filters); setSekcjaFiltrow(d.sekcja);
            } : undefined}
          />
        );
        if (kompakt) return <span key={klucz} className="contents">{chip}</span>;
        return (
          <Popover key={klucz} onOpenChange={(o) => { if (o) toast.dismiss(); }}>
            <PopoverTrigger asChild>{chip}</PopoverTrigger>
            <PopoverContent align="start" className={m.id === 'pokoje' ? 'w-[24rem] max-w-[calc(100vw-2rem)]' : 'w-80'}>
              {d.okienko}
            </PopoverContent>
          </Popover>
        );
      }
      if (m.typ === 'sugestia') {
        const d = sugestie[m.id];
        return (
          <ChipFiltra
            key={klucz}
            klucz={klucz}
            testId={`chip-${m.id}`}
            wariant={m.aktywny ? 'aktywny' : 'sugestia'}
            kompakt={kompakt}
            label={d.label}
            ikona={d.ikona}
            naUsun={usun(m.id)}
            ariaUsun={aria(d.label)}
            // Aktywny chip zostaje nazwany swoją etykietą: „Usuń filtr …” nosi już × obok, a testy i
            // czytnik szukają chipa po tym, co widać (nazwa „Usuń filtr …” zepsuła E2E 19.10).
            aria-label={m.aktywny ? undefined : t('search.filters.suggestion_aria', { name: d.label })}
            // Sugestia to filtr tak/nie: aktywna wyłącza się dotknięciem całego chipa (jak ×), nie otwiera panelu.
            onClick={m.aktywny ? usun(m.id) : () => setFilters(d.wlacz)}
          />
        );
      }
      const c = activeChips[m.indeks];
      return (
        <ChipFiltra
          key={klucz}
          klucz={klucz}
          wariant="aktywny"
          kompakt={kompakt}
          label={c.label}
          ikona={c.ikona}
          naUsun={() => setFilters(c.clear(filters))}
          ariaUsun={aria(c.label)}
          onClick={c.przelacznik ? () => setFilters(c.clear(filters)) : () => openSheet(c.sekcja)}
        />
      );
    });
  };
  const rzadFiltrow = (kompakt: boolean) => (
    <RzadFiltrow
      kompakt={kompakt}
      naWyczysc={doWyczyszczenia > 0 ? clearAll : null}
      naWszystkie={() => openSheet()}
      zakladka={mojeFiltry.widoczna ? <ZakladkaMojichFiltrow stan={mojeFiltry} kompakt={kompakt} /> : undefined}
    >
      {elementyRzedu(kompakt)}
    </RzadFiltrow>
  );

  /**
   * Otwiera panel filtrów; z `sekcja` — przewinięty do niej (dotknięcie chipa w rzędzie, propozycja
   * Maćka 6.10). Sekcje to wiersze panelu A2 (`data-sekcja` w SearchFilters).
   */
  const [sekcjaPanelu, setSekcjaPanelu] = useState<SekcjaFiltrow | null>(null);
  const openSheet = (sekcja?: SekcjaFiltrow) => {
    // Toast nigdy nie leży na panelu (plan §1.25): otwarcie panelu albo arkusza jednej sekcji
    // zamyka każdy komunikat na ekranie (także „Cofnij” po × lokalizacji).
    toast.dismiss();
    setDraftFilters(filters);
    setZapiszZPanelu(false);
    setSekcjaPanelu(sekcja ?? null);
    setSheetOpen(true);
  };
  /**
   * „Zmień w Moich filtrach” z profilu („Czego szukam”, plan E6.3): wejście ze znacznikiem w stanie
   * wpisu otwiera panel od razu. Czekamy na Moje filtry — panel zaczyna szkic od bieżących filtrów,
   * więc otwarty przed podstawieniem zestawu pokazałby pusty panel. Znacznik czytamy raz, przy
   * montowaniu: `useStanWyszukiwania` zdejmuje go z wpisu historii (bez powtórki przy odświeżeniu).
   */
  const location = useLocation();
  const [panelZProfilu, setPanelZProfilu] = useState(() => zamiarPaneluFiltrow(location.state));
  /**
   * EDYCJA MOICH FILTRÓW Z PROFILU (Maciek 20.10): ten sam panel nad przygaszoną wyszukiwarką, ale
   * jako „Edytuj Moje filtry” — bez pastylki i bez pola „Zapisz…”, w stopce „Anuluj” / „Zapisz”.
   * „Zapisz” zapisuje zbiór (ta sama droga co pole „Zapisz…”, z pytaniem o e-maile przy pierwszym
   * zapisie) i wraca krokiem wstecz do profilu z otwartym „Czego szukam”; ×, Esc i „Anuluj”
   * wracają bez zapisu.
   */
  const [edycjaZProfilu, setEdycjaZProfilu] = useState(() => zamiarEdycjiZProfilu(location.state));
  const wrocDoProfilu = () => {
    oznaczPowrotDoCzegoSzukam();
    if (location.key !== 'default') nawiguj(-1);
    else nawiguj('/profile');
  };
  // Zapis w toku → powrót, gdy się skończy; przy pierwszym zapisie najpierw pytanie o e-maile (C3).
  const [powrot, setPowrot] = useState<{ czekaNaPytanie: boolean } | null>(null);
  const pytanieWidziane = useRef(false);
  const zapiszIWrocDoProfilu = () => {
    if (!maCene(draftFilters)) return;
    const czekaNaPytanie = !!mojeWyszukiwanie && mojeWyszukiwanie.savedAt === null && !mojeWyszukiwanie.alerts.enabled;
    setFilters(draftFilters);
    if (!mojeFiltry.zapiszZbior(draftFilters)) return;
    pytanieWidziane.current = false;
    setPowrot({ czekaNaPytanie });
    setSheetOpen(false);
  };
  useEffect(() => {
    if (!powrot) return;
    if (mojeFiltry.pytanie) pytanieWidziane.current = true;
    const krok = krokPowrotu({
      czekaNaPytanie: powrot.czekaNaPytanie,
      sprawdza: mojeFiltry.sprawdza,
      blad: mojeFiltry.blad,
      pytanie: mojeFiltry.pytanie,
      pytanieWidziane: pytanieWidziane.current,
    });
    if (krok === 'czekaj') return;
    if (krok === 'zostan') {
      // Zapis odrzucony: komunikat o błędzie już jest, człowiek zostaje przy wyszukiwarce.
      setPowrot(null);
      setEdycjaZProfilu(false);
      return;
    }
    if (krok === 'czekaj_na_pytanie') {
      // Pytanie wjeżdża po CZAS_DO_PYTANIA_MS; gdyby nie przyszło (baza włączyła już e-maile), nie czekamy w nieskończoność.
      const id = window.setTimeout(() => { setPowrot(null); wrocDoProfilu(); }, CZAS_DO_PYTANIA_MS + 600);
      return () => window.clearTimeout(id);
    }
    setPowrot(null);
    wrocDoProfilu();
    // `wrocDoProfilu` jest tworzona co render; liczą się stany zapisu i pytania.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [powrot, mojeFiltry.sprawdza, mojeFiltry.blad, mojeFiltry.pytanie]);
  // Dzwonek alertów obok „Moje filtry”: zamyka panel jak × i otwiera blok alertów w Ustawieniach.
  const doUstawienAlertow = useOtworzUstawieniaAlertow(() => setSheetOpen(false));
  /** Zamknięcie panelu (×, Esc, tło) w trybie edycji = „Anuluj”: powrót do profilu bez zapisu. */
  const naZmianePanelu = (otwarty: boolean) => {
    if (!otwarty && edycjaZProfilu && !powrot) { wrocDoProfilu(); return; }
    setSheetOpen(otwarty);
  };
  useEffect(() => {
    if (!panelZProfilu || mojeWyszukiwanie === undefined) return;
    if (mojeWyszukiwanie.hasAny && !zProfilu) return;
    setPanelZProfilu(false);
    openSheet();
    // `openSheet` jest tworzona na nowo co render; liczy się moment, w którym zestaw już jest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelZProfilu, mojeWyszukiwanie, zProfilu]);
  useEffect(() => {
    if (!sheetOpen || !sekcjaPanelu) return;
    // Panel wjeżdża z animacją; sekcję szukamy po zamontowaniu treści.
    const id = window.setTimeout(() => {
      document.querySelector(`[data-sekcja="${sekcjaPanelu}"]`)?.scrollIntoView({ block: 'start' });
      setSekcjaPanelu(null);
    }, 60);
    return () => window.clearTimeout(id);
  }, [sheetOpen, sekcjaPanelu]);

  // „Pokaż N ofert” zapisze też zbiór (to samo warunkowanie, co w `applyDraft`).
  const zapisuje = mojeFiltry.widoczna && panelMoichFiltrow.zaznaczone;
  const applyDraft = () => {
    setFilters(draftFilters);
    zatwierdz(draftFilters);
    setSheetOpen(false);
    // Zaznaczone „Zapisz…” + „Pokaż” = „Zapisz” przy fladze (C2), z tym samym pierwszym zapisem i pytaniem o e-maile (C3).
    if (zapisuje) mojeFiltry.zapiszZbior(draftFilters);
  };

  /**
   * HISTORIA WYSZUKIWAŃ (28.09): zapis przy ZATWIERDZENIU — wybór obszaru, „Pokaż N ofert"
   * w arkuszu, otwarcie oferty z wyników — a nie przy każdym ruchu suwaka. Wybór wpisu
   * przywraca cały stan; widok (lista/mapa) zostaje ten, w którym człowiek jest.
   */
  const przywrocWyszukiwanie = (f: SourcePostFilters) => {
    const nowe = { ...f, view: filters.view };
    setFilters(nowe);
    zatwierdz(nowe);
  };
  const zmienObszary = (areas: SearchArea[]) => {
    const nowe = { ...filters, areas: areas.length ? areas : undefined, suburbs: undefined };
    setFilters(nowe);
    zatwierdz(nowe);
  };

  const clearAll = () => {
    // Zdejmuje WSZYSTKO, lokalizację też (plan §1.69, 14.10; do tej pory — od 28.09 —
    // lokalizacja zostawała). „Cofnij" w komunikacie przywraca cały poprzedni stan 1:1.
    const { czyste, ile, cofnij } = wyczyscZCofnieciem(filters, filterCount);
    // „Wyczyść" znaczy „nie chcę tego zestawu", więc dotyczy także startu z profilu —
    // inaczej filtry wracałyby przy następnym wejściu i wyglądało to na awarię. Najpierw
    // odrzucenie (ustawia domyślne bez widoku), potem czyste filtry z zachowanym widokiem.
    if (zProfilu) odrzucProfil();
    setDraftFilters(czyste);
    setFilters(czyste);
    // × to nie edycja: po nim flaga Moich filtrów nie wysuwa „Zapisz” (plan §1.3).
    const cofnijBaze = mojeFiltry.oznaczBezEdycji(czyste);
    setSheetOpen(false);
    if (ile > 0) {
      toast(t('search.cleared_toast', { count: ile }), {
        toasterId: TOASTER_WYSZUKIWARKI,
        // Komunikat niesie „Cofnij”: domyślne 4 s Sonnera to za mało, żeby przeczytać, co zniknęło,
        // i zdążyć cofnąć.
        duration: CZAS_KOMUNIKATU_Z_COFNIJ_MS,
        // Szkic też wraca: każde otwarcie panelu i tak go nadpisuje filtrami, ale po „Cofnij”
        // szkic i filtry nie mają się różnić nawet na chwilę (czyszczenie ustawiło oba).
        action: { label: t('search.area.undo'), onClick: () => { setDraftFilters(cofnij); setFilters(cofnij); cofnijBaze(); } },
      });
    }
  };

  /**
   * Sam obszar, bez reszty filtrów — drugie wyjście z pustego wyniku. Przy wybranej
   * dzielnicy albo narysowanym wielokącie to zwykle TEN filtr wyzerował listę, a
   * „Wyczyść filtry" zabrałoby przy okazji cenę i pokoje, które człowiek chce zachować.
   */
  const maObszar = !!(filters.areas?.length || filters.polygon || filters.suburbs?.length);
  const usunObszary = () =>
    setFilters({ ...filters, areas: undefined, suburbs: undefined, polygon: undefined });

  /**
   * × LOKALIZACJI (blok „Warszawa · …" i pole obszaru). Piotr 28.09: bez wyjaśnienia ×
   * wygląda jak kasowanie filtrów. Na pulpicie wyjaśnia to tooltip przy ×; na dotyku hovera
   * nie ma, więc po dotknięciu wychodzi komunikat „Pokazujesz całą Warszawę. Filtry zostały."
   * z „Cofnij", które przywraca lokalizację 1:1 (`zLokalizacja`).
   */
  const dotyk = useDotyk();
  const usunLokalizacje = () => {
    const poprzednia = lokalizacjaZ(filters);
    setFilters((f) => bezLokalizacji(f));
    if (!dotyk) return;
    toast(t('search.area.cleared_toast', { miasto: t(`search.area.miasto_biernik.${PRIMARY_ACTIVE_CITY}`, { defaultValue: PRIMARY_ACTIVE_CITY }) }), {
      toasterId: TOASTER_WYSZUKIWARKI,
      action: { label: t('search.area.undo'), onClick: () => setFilters((f) => zLokalizacja(f, poprzednia)) },
    });
  };

  /**
   * PUSTY WYNIK PO WEJŚCIU Z „MOJEGO WYSZUKIWANIA" (ustalenia 28.09): pokaż, co poluzować —
   * filtry od tego, który najbardziej zawęża, każdy z przyciskiem zdjęcia. Kolejność daje liczba
   * ofert po zdjęciu danego filtra, liczona TYLKO w tym stanie (pusto + start z profilu), tym
   * samym łańcuchem co lista; samej liczby nie pokazujemy („ile ukryto" to kuchnia).
   */
  const pustoZProfilu = zProfilu && !isLoading && !error && posts.length === 0;
  const kandydaciPoluzowania = useMemo(() => {
    const k = activeChips
      .map((c) => ({ label: c.label, filtry: c.clear(filters), naUsun: () => setFilters(c.clear(filters)) }));
    if (maObszar) {
      const nazwy = (filters.areas ?? []).map((a) => etykietaObszaru(a, t));
      k.push({ label: nazwy.join(', ') || t('search.history.drawn_area'), filtry: bezLokalizacji(filters), naUsun: () => usunLokalizacje() });
    }
    return k;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChips, filters, maObszar]);
  const licznikiPoluzowania = useLiczbySerp(kandydaciPoluzowania.map((k) => k.filtry), { enabled: pustoZProfilu });
  const poluzuj = pustoZProfilu
    ? uporzadkujPoluzowanie(kandydaciPoluzowania, licznikiPoluzowania.map((l) => l.data)).map(({ label, naUsun }) => ({ label, naUsun }))
    : undefined;

  // SERP toolbar (count + sort + neighbourhood-score toggle) lives in the sticky
  // header so it stays reachable while scrolling. The count tracks whichever
  // result set is on screen: pins in the mobile map-only view, the paginated
  // list everywhere else (desktop hybrid always has the list).
  const mobileMapView = view === 'map' && !isDesktop;
  /**
   * TELEFON, WIDOK MAPY = EKRAN, NIE STRONA. Dokument zablokowany (`html.mapa-pelna`, index.css),
   * przewinięcie wyzerowane — także przy pełnym wczytaniu `/search?view=map`, gdy przeglądarka
   * odtwarza pozycję z przewiniętej listy (zmierzone: scrollY 665, mapa poza ekranem). Stopka
   * w tym trybie się nie renderuje (`isFullScreenMapView`, ChromeFrame).
   * Kontrola: scripts/shot-steps/mapa-pelny-ekran.mjs.
   */
  useLayoutEffect(() => {
    if (!mobileMapView) return;
    const html = document.documentElement;
    html.classList.add('mapa-pelna');
    window.scrollTo(0, 0);
    // Odtworzenie pozycji przez przeglądarkę przychodzi czasem PO pierwszym układzie.
    const id = requestAnimationFrame(() => window.scrollTo(0, 0));
    return () => {
      cancelAnimationFrame(id);
      html.classList.remove('mapa-pelna');
    };
  }, [mobileMapView]);
  // Impuls „wejdź w tryb rysowania / wyboru dzielnic" z nakładki wyboru obszaru. Stan
  // trybu mieszka w MapView (to jego zachowanie), tu leży tylko jednorazowe żądanie,
  // które mapa kwituje po zastosowaniu. Dzięki temu przełączenie widoku na mapę i
  // wejście w tryb to jedno dotknięcie, a nie dwa.
  const [requestedMapMode, setRequestedMapMode] = useState<'draw' | 'districts' | null>(null);
  // PRZEGLĄD 27.09: noty zeszły z mapy — liczby przychodzą z MapView i pokazuje je ikona przy
  // liczniku ofert.
  // Kreska filtrów miękkich na liście (SRCH-24 v2) — pozycja i nagłówek z `filtryMiekkie.ts`.
  const pozycjaKreskiListy = useMemo(
    () => pozycjaKreski(posts.slice(0, visibleCount) as Parameters<typeof pozycjaKreski>[0], filters),
    [posts, visibleCount, filters],
  );
  // „Pozostałe N ofert” (Piotr 29.09): N = ze zdjęciem i z „nie wiadomo” — ta sama ścieżka i te same
  // filtry co lista (`useLiczbaSekcji`), sekcja bez zdjęć się nie liczy.
  const { data: podKreska } = useLiczbaSekcji(filters, 'podKreska', { enabled: pozycjaKreskiListy >= 0 });
  const naglowekKreskiListy =
    pozycjaKreskiListy >= 0 ? naglowekKreski(filters, pozycjaKreskiListy === 0, t, i18n.language, podKreska ?? null) : null;
  // Druga kreska: przed pierwszą ofertą bez zdjęć (Piotr 29.09, tekst W1).
  const pozycjaKreskiBezZdjecListy = useMemo(
    () => pozycjaKreskiBezZdjec(posts.slice(0, visibleCount)),
    [posts, visibleCount],
  );
  const [notyMapy, setNotyMapy] = useState<{ naMapie: number; brakuje: number; pozaKadrem: number } | null>(null);
  const [notyOtwarte, setNotyOtwarte] = useState(false);
  // Nagłówek mapy na telefonie (krok A): nakładka obszarów, mały arkusz jednej sekcji filtrów
  // i arkusz sortowania otwierają się stąd, bo w tym widoku nie ma ani pola, ani rzędu
  // licznika, w których mieszkały wcześniej.
  /**
   * „Warszawa · Muranów, Wilanów" — GDZIE szukamy, jednym zdaniem.
   *
   * Miasto zawsze na początku, bo bez niego nazwy dzielnic nic nie znaczą komuś, kto wszedł z
   * linku. Obszary po przecinku w kolejności dodania; gdy nic nie wybrano, zostaje samo
   * miasto — to jest prawda o zakresie, a nie pusty napis.
   */
  const podsumowanieMiejsca = useMemo(() => {
    const nazwy = (filters.areas ?? []).map((a) => etykietaObszaru(a, t));
    return nazwy.length ? `Warszawa · ${nazwy.join(', ')}` : 'Warszawa';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.areas]);
  const [nakladkaObszarow, setNakladkaObszarow] = useState(false);
  const [sortowanieOtwarte, setSortowanieOtwarte] = useState(false);
  const zadajTrybMapy = (tryb: 'draw' | 'districts') => {
    setRequestedMapMode(tryb);
    if (view !== 'map') setFilters({ ...filters, view: 'map' });
  };
  /**
   * ILE OFERT MA WYNIK — jedna liczba dla wszystkich widoków. Od 27.09 zapytanie o piny
   * odcina oferty, których nie da się narysować (bez współrzędnych I bez dzielnicy), więc
   * jego `count` opisuje PODZBIÓR rysowalny: na telefonie w widoku mapy nagłówek pokazywał
   * przez to 3 873, a lista dla tych samych filtrów 4 079. Dwie liczby o jednym wyniku to
   * dokładnie ten rodzaj rozjazdu, który potem tłumaczy się użytkownikowi w zgłoszeniu.
   * W widoku mapy bierzemy więc osobny, tani licznik (`head` + `count`, bez wierszy), a
   * różnicę między nim a liczbą pinów pokazuje nota „Na mapie X z Y" — i to jest jej
   * właściwa treść.
   */
  const { data: liczbaWyniku } = useSerpCount(filters, { enabled: mobileMapView });
  // Pastylka „N ofert bez adresu · pokaż na liście” zdjęta (Piotr 1.10): od 30.09 oferty bez adresu
  // siedzą w kołach dzielnic i w kole „lokalizacja nieznana”, więc suma kół = licznik bez niej.
  // Filtr `bezAdresu=1` z adresu dalej działa (stare linki, chip na liście).
  const przyciskiMapyUstalone = true;
  const pastylkaBezAdresu = null;
  const wynikRazem = mobileMapView ? liczbaWyniku ?? null : totalCount;

  const headerResultCount = mobileMapView
    ? (liczbaWyniku ?? pins.length)
    : (totalCount ?? posts.length);
  // Licznik PŁYNIE do nowej wartości przy każdym przefiltrowaniu (plan E5.1, §1.33): liczba do
  // pokazania idzie przez tween, słowo po niej („oferta/oferty/ofert”) od razu z wartości końcowej.
  const headerCountShown = useAnimatedCount(isLoading ? null : headerResultCount) ?? headerResultCount;

  // STAŁE SZEROKOŚCI górnego rzędu (Piotr 7.10). Licznik: najdłuższy zapis do 200 000 w każdej
  // formie liczby mnogiej bieżącego języka (1, 2, 5, 22 wybierają formy one/few/many/other).
  const wariantyLicznika = useMemo(
    () => [1, 2, 5, 22].map((n) => t('search.result_count', { count: n, liczba: formatLiczby(200000, i18n.language) })),
    [t, i18n.language],
  );
  // Najdłuższe napisy „Pokaż N ofert” (każda forma liczby mnogiej, czterocyfrowa liczba) — szerokość
  // przycisku w arkuszu jednej sekcji nie skacze, gdy licznik się przewija.
  const wariantyPrzyciskuOfert = useMemo(
    () => [
      ...[1, 2, 5, 22].map((n) => t('search.apply_count', { count: n, liczba: formatLiczby(8888, i18n.language) })),
      t('search.show_offers'),
    ],
    [t, i18n.language],
  );
  const etykietaSortowania = (klucz: string) =>
    t('search.sort_by', { sort: t(`search.sort.${klucz}`).toLocaleLowerCase(i18n.language) });
  const wariantySortowania = SORTOWANIA.map((k) => etykietaSortowania(k));

  /**
   * NOTY O WYNIKU ZNIKNĘŁY (decyzja Piotra 28.09): „ile kwot jest szacunkiem" i „ile
   * ofert pominięto" to kuchnia, a nie informacja, która zmienia następny ruch człowieka.
   * Szacunek widać tam, gdzie ma znaczenie — przy konkretnej kwocie na karcie („ok.").
   *
   * ZOSTAJE JEDEN WYJĄTEK, i tylko dlatego, że niesie DZIAŁANIE: gdy w trybie kosztu
   * reguła SRCH-15 zostawia co najwyżej dziesięć ofert, a bez niej byłoby ich więcej,
   * pokazujemy linijkę „Pokaż też oferty bez informacji o opłatach". Bez niej człowiek
   * widzi pustkę i nie ma jak się dowiedzieć, że da się ją cofnąć.
   */
  const pustoPrzezOplaty =
    filters.costMode === 'total' &&
    !filters.nieznaneOplaty &&
    headerResultCount <= 10 &&
    !!bezOplat &&
    bezOplat > 0;

  return (
    // Extra bottom padding on mobile so the floating list/map pill never covers
    // the last card; desktop has no pill so it resets.
    <div
      className={cn('overflow-x-clip lg:pb-10', mobileMapView ? 'pb-0' : 'pb-24')}
      // Otwarcie oferty z wyników (karta na liście, karuzela na mapie) zatwierdza wyszukiwanie
      // — zapis do historii. Jedno przechwycenie zamiast przekazywania do każdej karty.
      onClickCapture={(e) => {
        if ((e.target as HTMLElement).closest?.('a[href^="/offer/"]')) zapiszWyszukiwanie(filtersRef.current);
      }}
    >
      {/* Sticky search header */}
      {/* Floats just below the fixed global nav (h-14) and stays on top while
          scrolling — z-30 keeps it above the list/map but under the nav (z-50). */}
      <div
        ref={headerRef}
        // `--app-header-offset` (Header.tsx) = gdzie naprawdę kończy się nagłówek strony: 0,
        // gdy odjechał na wynikach pulpitowych, i z uwzględnieniem przesunięcia widoku Safari.
        // Blok przykleja się pod nim JAKO CAŁOŚĆ — nigdy nie jest widoczny w połowie.
        // Tło PEŁNE, nie `bg-white/95` + rozmycie: przy przyklejonym bloku zdjęcia kart
        // prześwitywały pod polem obszaru (zmierzone na zrzucie iPada 28.09).
        className={`relative sticky top-[var(--app-header-offset,var(--app-header-h,3.5rem))] z-30 border-b bg-white transition-[top,box-shadow] duration-200 ease-out ${
          przyklejony ? 'shadow-md' : ''
        }`}
      >
        {/* Results are being recomputed. Sits ON the header's bottom border,
            absolutely positioned, so it costs zero layout — the header is
            measured for the sticky map offset and must not change height.
            Placed here rather than over the list because it has to speak for
            the map view too (mobile map mode has no list on screen at all). */}
        {refreshing && (
          <div
            className="absolute inset-x-0 -bottom-px h-0.5 animate-pulse bg-primary"
            aria-hidden
          />
        )}
        <span className="sr-only" role="status" aria-live="polite">
          {refreshing ? t('search.updating') : ''}
        </span>
        {/* Capped to the shared app width (max-w-content) with the same px-4
            gutter as the global Header, so the search bar/map edges line up
            with the nav instead of spilling past it. */}
        <Sheet open={sheetOpen} onOpenChange={naZmianePanelu}>
        <div className="mx-auto max-w-content px-4">
          {/* Row 1: area search. Filtry sits beside it on desktop; on mobile it
              drops to the controls row (row 3) so the search field keeps its own
              full line. The list/map toggle is a floating bottom pill. */}
          {/* NAGŁÓWEK MOBILNY — JEDEN DLA OBU WIDOKÓW (decyzja Piotra 27.09). Do tej pory
              lista miała własny zestaw (pole wyszukiwania + rząd chipów + rząd licznika), a
              mapa tę kartę podsumowania; przełącznik Lista/Mapa zmieniał więc nie tylko to,
              co widać pod spodem, ale i sposób sterowania tymi samymi filtrami. Rzędy 1–3
              renderują się od tej pory WYŁĄCZNIE na pulpicie — nie chowamy ich klasą, bo
              schowany rząd zostaje w drzewie i bramka znajduje w nim drugi znacznik filtrów
              oraz drugie „Wyczyść". */}
          {!isDesktop && (
            <NaglowekMobilny
              miejsce={podsumowanieMiejsca}
              // `null` w czasie pierwszego liczenia — nagłówek pokazuje wtedy szkielet,
              // a nie „0 ofert". Zero jest odpowiedzią serwera, nie stanem wczytywania.
              liczbaOfert={isLoading ? null : headerResultCount}
              // Zdanie w karcie („3 filtry”) liczy wszystkie ustawione filtry poza lokalizacją.
              liczbaWszystkichFiltrow={filterCount}
              notyWyniku={[]}
              // „· moje wyszukiwanie” w karcie nosiło to samo co dawny chip; od C1 mówi o tym flaga,
              // a karta liczy filtry jak w makiecie („N ofert · k filtry”).
              // Noty o pinach mówią o MAPIE (ile pominiętych, ile poza kadrem) — na
              // liście nie mają o czym mówić, więc ikona informacji tam nie wchodzi.
              noty={mobileMapView ? notyMapy : null}
              naObszar={() => { toast.dismiss(); setNakladkaObszarow(true); }}
              rzadFiltrow={rzadFiltrow(true)}
              naUsunObszar={maObszar ? usunLokalizacje : undefined}
              // ⇅ w karcie (układ filtrów 6.10) — tylko lista; otwiera arkusz sortowania.
              sortowanie={mobileMapView ? null : {
                etykieta: t('search.sort_by', { sort: t(`search.sort.${filters.sort ?? 'newest'}`).toLocaleLowerCase(i18n.language) }),
                aktywne: (filters.sort ?? 'newest') !== 'newest',
                naOtworz: () => setSortowanieOtwarte(true),
              }}
            />
          )}
          {isDesktop && (
          <div className="flex items-center gap-2 py-3 lg:gap-3">
            <div className="min-w-0 flex-1" data-testid="pole-wyszukiwania-blok">
              <SearchAreaBar
                areas={filters.areas ?? []}
                onChange={zmienObszary}
                onPrzywroc={przywrocWyszukiwanie}
                onDrawArea={() => zadajTrybMapy('draw')}
                onPickDistricts={() => zadajTrybMapy('districts')}
                filters={filters}
                naWyczyscLokalizacje={maObszar ? usunLokalizacje : undefined}
              />
            </div>
            {/* LICZBA WYNIKÓW I SORTOWANIE jako cichy tekst na końcu górnego rzędu (propozycja
                Maćka 6.10): górny rząd mówi GDZIE, ILE i W JAKIEJ KOLEJNOŚCI, rząd niżej to same
                filtry. Licznik JEST nagłówkiem pierwszego poziomu (SRCH-05).
                STAŁA SZEROKOŚĆ BLOKU (Piotr 7.10, „nie może przeskakiwać”): licznik ma szerokość
                najdłuższego zapisu do „200 000 ofert” we wszystkich formach liczby mnogiej bieżącego
                języka (wyrównany do prawej, cyfry tabelaryczne), ⓘ ma miejsce zawsze (niewidoczne,
                gdy nie ma czego wyjaśniać), sortowanie — szerokość najdłuższej z czterech opcji.
                Dzięki temu pole wyszukiwania (flex-1) nie zmienia szerokości. Strażnik:
                e2e/features/wyszukiwarka/naglowek-stabilny.feature. */}
            {!error && (
              <div data-testid="blok-licznika" className="flex shrink-0 items-center gap-1 text-base text-muted-foreground">
                {/* Warianty szerokości stoją OBOK `h1`, nie w nim: tekst nagłówka ma być samą liczbą. */}
                <StalaSzerokosc warianty={wariantyLicznika} wyrownanie="end" blok className="shrink-0 tabular-nums">
                <h1
                  data-kotwica="licznik"
                  data-testid="licznik-ofert"
                  className={cn(
                    'font-normal tabular-nums transition-opacity duration-200',
                    (refreshing || isLoading) && 'opacity-40',
                  )}
                >
                    {isLoading ? (
                      <>
                        <span className="sr-only">{t('search.title')}</span>
                        <span
                          aria-hidden
                          data-testid="licznik-wczytywanie"
                          className="inline-block h-4 w-28 animate-pulse rounded bg-muted align-middle"
                        />
                      </>
                    ) : (
                      t('search.result_count', { count: headerResultCount, liczba: formatLiczby(headerCountShown, i18n.language) })
                    )}
                </h1>
                </StalaSzerokosc>
                {/* ⓘ — miejsce ZAWSZE zarezerwowane; bez noty niewidoczne i poza kolejnością Tab. */}
                <span className="relative shrink-0" data-testid="nota-wyniku">
                  {(() => {
                    const jest = !!(notyMapy && notyMapy.pozaKadrem > 0);
                    return (
                      <>
                        <button
                          type="button"
                          onClick={() => setNotyOtwarte((v) => !v)}
                          aria-label={t('search.map.notes_aria')}
                          aria-hidden={!jest || undefined}
                          tabIndex={jest ? undefined : -1}
                          disabled={!jest}
                          className={cn(
                            'flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted',
                            !jest && 'invisible',
                          )}
                        >
                          <Info className="h-4 w-4" />
                        </button>
                        {jest && notyOtwarte && (
                          <span className="absolute right-0 top-8 z-50 w-64 rounded-xl bg-foreground p-3 text-xs leading-relaxed text-background shadow-lg">
                            <span className="block">{t('search.map.outside_view', { count: notyMapy!.pozaKadrem })}</span>
                          </span>
                        )}
                      </>
                    );
                  })()}
                </span>
                <Select
                  value={filters.sort ?? 'newest'}
                  onValueChange={(v) =>
                    setFilters({ ...filters, sort: v === 'newest' ? undefined : (v as SourcePostFilters['sort']) })
                  }
                >
                  <SelectTrigger
                    data-testid="sortuj-tekst"
                    className="h-9 w-auto gap-1 border-0 bg-transparent px-1 text-base text-muted-foreground shadow-none hover:text-foreground focus:ring-0"
                    aria-label={t('search.sort.label')}
                  >
                    {/* Treść do PRAWEJ (Maciek 19.10): szewron stoi tuż za etykietą, a zapas szerokości
                        najdłuższej opcji wypada po lewej — między licznikiem a „Sortuj”. Pudełko przycisku
                        się nie zmienia (naglowek-stabilny), więc nic nie przeskakuje. Bez kropki „·”. */}
                    <StalaSzerokosc warianty={wariantySortowania} wyrownanie="end" testIdTresci="sortuj-wartosc">
                      {etykietaSortowania(filters.sort ?? 'newest')}
                    </StalaSzerokosc>
                  </SelectTrigger>
                  <SelectContent align="end">
                    {SORTOWANIA.map((key) => (
                      <SelectItem key={key} value={key}>
                        {t(`search.sort.${key}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          )}

          {/* RZĄD FILTRÓW (propozycja Maćka 6.10) jest jeden: pod polem wyszukiwania na pulpicie,
              pod kartą na telefonie (`RzadFiltrow`). Dawny osobny rząd chipów i „+N” w rzędzie
              licznika zniknęły — każdy aktywny filtr widać dokładnie raz. */}

          {/* Row 3: result count + neighbourhood-score toggle + sort. Shown
              whenever the search has resolved — INCLUDING a 0-result set ("0 ofert")
              — so the sticky header keeps a stable height and the map/list below
              don't jump when a filter empties the results. */}
          {isDesktop && (
            <div data-testid="rzad-licznika" className="pb-3">
              {rzadFiltrow(false)}
            </div>
          )}
        </div>
          {/* Desktop: filters slide in from the LEFT as an overlay panel
              (quintoandar-style), leaving the list+map dimmed behind. Phone: the panel
              covers the WHOLE screen (Maciek 15.10, plan E3.1): header „Filtry” + slot for the
              „Moje filtry” pill + ×, footer pinned, no grip. The one-section chip sheets
              (Cena / Pokoje) stay short bottom sheets. */}
          <SheetContent
            onOpenAutoFocus={(e) => {
              // Fokus na ARKUSZ, nie na pierwszy przycisk w nim (28.09): na dotyku automatyczny
              // fokus „Cena najmu" rysował pierścień, który wyglądał jak błąd. Klawiatura dalej
              // zaczyna od arkusza i Tab prowadzi do pierwszego przycisku.
              e.preventDefault();
              (e.currentTarget as HTMLElement | null)?.focus?.({ preventScroll: true });
            }}
            side={isDesktop ? 'left' : 'bottom'}
            className={cn(
              // Arkusz dostaje fokus przy otwarciu (patrz wyżej) — to kontener, nie kontrolka,
              // więc bez obrysu przeglądarki.
              'flex flex-col focus:outline-none',
              isDesktop
                ? 'h-full w-full sm:max-w-md'
                // PEŁNY EKRAN na telefonie (wzór qasa/QuintoAndar), a nie arkusz na 85%
                // wysokości. Filtry to osobne zadanie, a nie zaglądanie pod spód: przy 85%
                // widać pasek wyników nad arkuszem, oko wraca do niego co chwilę, a każda
                // sekcja i tak wymaga przewijania. Pełny ekran daje temu zadaniu cały ekran i
                // jedno wyjście — „Pokaż N ofert". Bez zaokrągleń, bez górnej kreski i bez
                // uchwytu: to ekran, nie arkusz. Stopka trzyma odstęp od paska systemowego.
                : 'inset-0 h-[100dvh] max-h-[100dvh] rounded-none border-0 pb-[max(1.5rem,env(safe-area-inset-bottom))]',
            )}
          >
            <SheetHeader className="flex-row items-center gap-3 space-y-0 pr-10 text-left">
              {/* Tytuł panelu: 20 px na telefonie, 18 na pulpicie — skala panelu (29.09). */}
              <SheetTitle className="text-xl md:text-lg">
                {t(edycjaZProfilu ? 'search.my_filters.edit_title' : 'search.filters_title')}
              </SheetTitle>
              {/* MIEJSCE NA PASTYLKĘ „MOJE FILTRY” (C2, plan §1.4): między tytułem a ×. Puste do
                  czasu C2, ale zarezerwowane, żeby nagłówek nie zmienił układu, gdy dojdzie. */}
              <div data-slot="moje-filtry" className="flex min-h-9 flex-1 items-center justify-end gap-2">
                {mojeFiltry.widoczna && !edycjaZProfilu && (
                  <PastylkaMoichFiltrow stan={panelMoichFiltrow} opis={mojeFiltry.opis} naKlik={naPastylke} />
                )}
                {/* Obok pastylki i tylko razem z nią: stan alertów e-mail + skrót do Ustawień. */}
                <DzwonekAlertow
                  widoczny={mojeFiltry.widoczna && !edycjaZProfilu}
                  alerty={mojeWyszukiwanie?.alerts}
                  naKlik={doUstawienAlertow}
                />
              </div>
            </SheetHeader>
            {/* Only the controls scroll — header and the Clear/Apply footer stay
                pinned, so the apply button is never below the fold. */}
            <div className="-mx-6 flex-1 overflow-y-auto px-6 py-4">
              {/* Oceny okolicy (wyłączone, POKAZ_OCENY_OKOLICY) — w panelu na obu układach od 6.10,
                  bo rzędu licznika z przełącznikiem na pulpicie już nie ma. Preferencja widoku —
                  działa od razu, nie przez szkic. */}
              {POKAZ_OCENY_OKOLICY && (
                <button
                  type="button"
                  onClick={() => setShowPoiBadges((v) => !v)}
                  aria-pressed={showPoiBadges}
                  className={`mb-4 flex h-10 w-full items-center justify-center gap-1.5 rounded-full text-sm font-medium transition-colors ${
                    showPoiBadges ? 'bg-primary/10 text-primary' : 'bg-muted/40 text-muted-foreground'
                  }`}
                >
                  <Gauge className="h-4 w-4" />
                  {t('search.toggle_poi_badges')}
                </button>
              )}
              <SearchFilters
                filters={draftFilters}
                onChange={setDraftFilters}
                zwijane
              />
            </div>
            {/* Linijka nad przyciskami — tylko przy filtrze miękkim (nagłówek nad listą bez „X z Y”,
                zasada Piotra z 28.09; tu jest informacją o szkicu, nie o wynikach). */}
            {miekkieWSzkicu && potwierdzoneWSzkicu != null && razemWSzkicu != null && (
              <p data-testid="potwierdza-cechy" className="text-balance border-t pt-3 text-center text-xs text-muted-foreground">
                {/* Liczba mnoga i orzeczenie idą za LICZBĄ SPEŁNIAJĄCYCH („3 z 50 spełniają”, „5 z 50
                    spełnia”), nie za całością. */}
                {t('search.filters.potwierdza', {
                  count: potwierdzoneWSzkicu,
                  n: formatLiczby(potwierdzoneWSzkicu, i18n.language),
                  liczba: formatLiczby(razemWSzkicu, i18n.language),
                })}
                {/* Piotr 29.09: samo „N z M potwierdza” nie mówiło, co z resztą. Reszta to
                    ogłoszenia, które o wybranych cechach nie wspominają (sprzeczne są już
                    odsiane) — lista pokazuje je pod kreską „Pozostałe N ofert”. */}
                {potwierdzoneWSzkicu < razemWSzkicu && (
                  <> {t('search.filters.potwierdza_reszta', {
                    count: razemWSzkicu - potwierdzoneWSzkicu,
                    reszta: formatLiczby(razemWSzkicu - potwierdzoneWSzkicu, i18n.language),
                  })}</>
                )}
              </p>
            )}
            {mojeFiltry.widoczna && !edycjaZProfilu && (
              <PoleZapisuMoichFiltrow stan={panelMoichFiltrow} maZbior={panelMoichFiltrow.pigulkaAktywna} naZmiane={setZapiszZPanelu} />
            )}
            {edycjaZProfilu ? (
              <>
                {/* Liczba ofert zostaje jako podpowiedź; bez ceny zbioru nie da się zapisać (§1.3). */}
                <p data-testid="edycja-z-profilu-podpowiedz" className="border-t pt-3 text-center text-xs text-muted-foreground">
                  {!maCene(draftFilters)
                    ? t('search.my_filters.need_price_save')
                    : draftCount != null
                      ? t('search.my_filters.edit_count', { count: draftCount, liczba: formatLiczby(draftCountShown ?? draftCount, i18n.language) })
                      : null}
                </p>
                <SheetFooter className="flex-row justify-between gap-2 border-t pt-3">
                  <Button variant="ghost" data-testid="edycja-z-profilu-anuluj" className="h-10 text-base md:h-9 md:text-sm" onClick={wrocDoProfilu}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    data-testid="edycja-z-profilu-zapisz"
                    disabled={!maCene(draftFilters) || mojeFiltry.sprawdza}
                    onClick={zapiszIWrocDoProfilu}
                    className="h-10 flex-1 text-base md:h-9 md:text-sm"
                  >
                    {t('common.save')}
                  </Button>
                </SheetFooter>
              </>
            ) : (
            <SheetFooter className="flex-row justify-between gap-2 border-t pt-3">
              {/* Czyści tylko SZKIC, lokalizację też (plan §1.69); okno zostaje otwarte, a zmiana
                  wchodzi dopiero z „Pokaż N ofert" — jak każda inna zmiana w szkicu. */}
              <Button variant="ghost" data-testid="wyczysc-szkic" className="h-10 text-base md:h-9 md:text-sm" onClick={() => { setDraftFilters(wyczyszczoneFiltry(draftFilters)); setZapiszZPanelu(false); }}>
                {t('search.clear')}
              </Button>
              <SheetClose asChild>
                {/* Makieta: „Pokaż N ofert” wypełnia stopkę obok „Wyczyść” (`.btn.solid{flex:1}`). */}
                <Button onClick={applyDraft} className="h-10 flex-1 text-base tabular-nums md:h-9 md:text-sm">
                  {/* Zaznaczone „Zapisz zmiany w moich filtrach”: przycisk mówi, że też zapisze (Maciek 20.10). */}
                  {draftCount != null
                    ? t(zapisuje ? 'search.apply_save_count' : 'search.apply_count', { count: draftCount, liczba: formatLiczby(draftCountShown ?? draftCount, i18n.language) })
                    : t(zapisuje ? 'search.save_and_show_offers' : 'search.show_offers')}
                </Button>
              </SheetClose>
            </SheetFooter>
            )}
          </SheetContent>
        </Sheet>

        {/* Nakładka obszarów otwierana z nagłówka mapy — w tym widoku nie ma pola
            wyszukiwania, w którym mieszkał jej przycisk. */}
        {nakladkaObszarow && (
          <SearchAreaOverlay
            areas={filters.areas ?? []}
            onChange={(areas) =>
              setFilters({ ...filters, areas: areas.length ? areas : undefined, suburbs: undefined })
            }
            onPrzywroc={przywrocWyszukiwanie}
            // Na telefonie obszary wybiera się serią dotknięć — zapisujemy stan ZATWIERDZONY,
            // czyli przy zamknięciu nakładki, a nie każdy pośredni wybór.
            onClose={() => { setNakladkaObszarow(false); zatwierdz(filtersRef.current); }}
            filters={filters}
            onDrawArea={() => { setNakladkaObszarow(false); zadajTrybMapy('draw'); }}
            onPickDistricts={() => { setNakladkaObszarow(false); zadajTrybMapy('districts'); }}
          />
        )}

        {/* MAŁY ARKUSZ JEDNEJ SEKCJI (wzór Yandex): skrót „Cena" albo „Pokoje" otwiera samą
            tę sekcję, a nie cały arkusz filtrów. Te dwa rusza się najczęściej i droga przez
            pełny arkusz — otwórz, znajdź sekcję, przewiń, zastosuj — była dłuższa niż sama
            zmiana. Przycisk zatwierdzenia nosi ten sam licznik co wszędzie indziej. */}
        <Sheet open={sekcjaFiltrow !== null} onOpenChange={(o) => !o && setSekcjaFiltrow(null)}>
          <SheetContent side="bottom" className="rounded-t-2xl focus:outline-none" onOpenAutoFocus={(e) => {
              // Fokus na ARKUSZ, nie na pierwszy przycisk w nim (28.09): na dotyku automatyczny
              // fokus „Cena najmu" rysował pierścień, który wyglądał jak błąd. Klawiatura dalej
              // zaczyna od arkusza i Tab prowadzi do pierwszego przycisku.
              e.preventDefault();
              (e.currentTarget as HTMLElement | null)?.focus?.({ preventScroll: true });
            }}>
            <SheetHeader>
              <SheetTitle>
                {t(sekcjaFiltrow === 'cena' ? 'search.filters.price' : sekcjaFiltrow === 'metraz' ? 'search.filters.area' : 'search.filters.rooms')}
              </SheetTitle>
            </SheetHeader>
            <div className="py-4">
              {sekcjaFiltrow === 'cena' ? (
                <PriceControl filters={draftFilters} onChange={setDraftFilters} bezEtykiety />
              ) : sekcjaFiltrow === 'metraz' ? (
                <AreaControl filters={draftFilters} onChange={setDraftFilters} bezEtykiety />
              ) : (
                <RoomsControl filters={draftFilters} onChange={setDraftFilters} bezEtykiety />
              )}
            </div>
            <SheetFooter className="flex-row justify-between gap-2 border-t pt-3">
              <Button variant="ghost" onClick={() => setSekcjaFiltrow(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                data-testid="zastosuj-arkusz-sekcji"
                onClick={() => { setFilters(draftFilters); zatwierdz(draftFilters); setSekcjaFiltrow(null); }}
                className="tabular-nums"
              >
                {/* Stała szerokość (Maciek 20.10): licznik się przewija, a przycisk rósł i malał z każdą
                    cyfrą i drżał. Rezerwujemy miejsce na najdłuższy wariant, cyfry mają równą szerokość. */}
                <StalaSzerokosc warianty={wariantyPrzyciskuOfert} wyrownanie="center">
                  {draftCount != null
                    ? t('search.apply_count', { count: draftCount, liczba: formatLiczby(draftCountShown ?? draftCount, i18n.language) })
                    : t('search.show_offers')}
                </StalaSzerokosc>
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Sortowanie: na telefonie w nagłówku mapy jest samą ikoną, więc etykiety muszą być
            gdzieś do przeczytania — tutaj. */}
        <Sheet open={sortowanieOtwarte} onOpenChange={setSortowanieOtwarte}>
          <SheetContent side="bottom" className="rounded-t-2xl focus:outline-none" onOpenAutoFocus={(e) => {
              // Fokus na ARKUSZ, nie na pierwszy przycisk w nim (28.09): na dotyku automatyczny
              // fokus „Cena najmu" rysował pierścień, który wyglądał jak błąd. Klawiatura dalej
              // zaczyna od arkusza i Tab prowadzi do pierwszego przycisku.
              e.preventDefault();
              (e.currentTarget as HTMLElement | null)?.focus?.({ preventScroll: true });
            }}>
            <SheetHeader>
              <SheetTitle>{t('search.sort.label')}</SheetTitle>
            </SheetHeader>
            <div className="flex flex-col py-2">
              {(['newest', 'oldest', 'cheapest', 'expensive'] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setFilters({ ...filters, sort: key === 'newest' ? undefined : key });
                    setSortowanieOtwarte(false);
                  }}
                  className={cn(
                    'rounded-xl px-3 py-3 text-left text-sm',
                    (filters.sort ?? 'newest') === key ? 'bg-primary/10 font-semibold text-primary' : 'text-foreground',
                  )}
                >
                  {t(`search.sort.${key}`)}
                </button>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="mx-auto max-w-content px-4 lg:flex lg:items-start">
        {/* LEFT: list column — full-width on mobile, flex-1 on desktop. Matches
            the header container (max-w-content with the nav's px-4 gutter). */}
        <div className="w-full py-4 md:py-6 lg:min-w-0 lg:flex-1 lg:pr-6">
        {/* JEDYNY KOMUNIKAT O WYNIKU, który został (decyzja Piotra 28.09) — bo niesie
            DZIAŁANIE. Reguła SRCH-15 potrafi zostawić garstkę ofert albo pustkę; wtedy
            człowiek musi wiedzieć, że da się to cofnąć, i móc zrobić to jednym ruchem.
            Bez liczb „ile pominięto" — to kuchnia. */}
        {pustoPrzezOplaty && !isLoading && (
          <button
            type="button"
            data-testid="pokaz-nieznane-oplaty"
            onClick={() => setFilters({ ...filters, nieznaneOplaty: true })}
            className="mb-4 text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            {t('search.show_unknown_fees')}
          </button>
        )}

        {/* SZKIELETY W KSZTAŁCIE KARTY, nie „trzy paski". Karta to zdjęcie 4:3, dzielnica,
            wiersz „pokoje · metraż", cena i linijka „na rynku od" — szkielet ma tyle samo
            wierszy, więc przyjście danych nie przesuwa niczego w dół. W widoku mapy na
            telefonie szkieletów nie ma: tam od razu pokazujemy podkład mapy (niżej). */}
        {isLoading && !(view === 'map' && !isDesktop) && (
          <div className="grid gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <div className="aspect-[4/3] w-full animate-pulse rounded-2xl bg-muted" />
                <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
                <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
                <div className="h-5 w-2/5 animate-pulse rounded bg-muted" />
                <div className="h-3 w-3/5 animate-pulse rounded bg-muted" />
              </div>
            ))}
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive">
            {t('search.error', { message: (error as Error).message })}
          </p>
        )}

        {/* PUSTY WYNIK POZA MAPĄ. W widoku mapy na telefonie ten sam komunikat leży NA
            mapie (niżej), żeby mapa i jej przyciski zostały na ekranie — bez tego zero
            wyników zabierało „Rysuj", „Dzielnice" i +/− razem z mapą (MOB-01, Maciek). */}
        {!isLoading && !error && !(view === 'map' && !isDesktop) && posts.length === 0 && (
          <PustyWynik naWyczysc={clearAll} naUsunObszar={maObszar ? usunObszary : undefined} poluzuj={poluzuj} />
        )}

        {/* WYSOKOŚĆ MAPY LICZONA, NIE ZGADYWANA (krok A). Stało tu `calc(100dvh-140px)` —
            stała wzięta z układu, który miał trzy rzędy nagłówka. Po skróceniu nagłówka do
            dwóch linijek mapa nadal kończyła się 107 px pod krawędzią ekranu, bo 140 nie ma
            nic wspólnego z tym, ile miejsca faktycznie zostało. Teraz odejmujemy to, co
            naprawdę stoi nad mapą: pasek aplikacji i zmierzony nagłówek wyszukiwarki. */}
        {/* MAPA RYSUJE SIĘ, ZANIM PRZYJDĄ PINY (krok N). Do 27.09 warunek brzmiał
            „są piny i nie wczytujemy", więc na wolnym łączu przez kilka sekund nie było
            ani mapy, ani szkieletu mapy — tylko pusty ekran pod nagłówkiem. Podkład,
            obrys miasta i przyciski są użyteczne od pierwszej klatki; piny dochodzą. */}
        {!error && view === 'map' && !isDesktop && (
          // Reszta ekranu: minus pasek aplikacji, zmierzony nagłówek wyszukiwarki, `py-4` kolumny
          // (2 × 16 px — dół zostaje, żeby zaokrąglone rogi mapy były widoczne) i dolna strefa
          // bezpieczna. Do 29.09 odejmowaliśmy tylko nagłówki i mapa kończyła się 16 px pod
          // krawędzią ekranu, a dokument dało się przewinąć.
          <div className="relative" style={{ height: `calc(100dvh - var(--app-header-offset, var(--app-header-h, 3.5rem)) - ${headerH}px - 2rem - env(safe-area-inset-bottom, 0px))` }}>
          <Suspense
            fallback={<div className="h-full w-full animate-pulse rounded-xl bg-muted/40" />}
          >
            <MapView
              fill
              bezAdresu={pastylkaBezAdresu}
              pins={pins}
              focusBounds={focusBounds}
              kamera={kameraStartowa}
              naKamere={naKamere}
              pomijajDopasowanie={!!kameraStartowa}
              otwartaOferta={widokMapy.oferta}
              naOtwartaOferte={naOtwartaOferte}
              boundaries={boundaries}
              stations={stations}
              lacznieOfert={wynikRazem}
              onNoty={setNotyMapy}
              przelacznikNaEkranie={!isDesktop}
              trybKosztu={filters.costMode === 'total'}
              filtryMiekkie={filters}
              pinyAktualne={!pinsQ.isPlaceholderData && (!pinsQ.isFetching || pinsQ.isFetchingNextPage)}
              pinyKompletne={pinsQ.data?.kompletne ?? false}
              przyciskiUstalone={przyciskiMapyUstalone}
              drawnPolygon={filters.polygon ?? null}
              onDrawnPolygonChange={(poly) => setFilters({ ...filters, polygon: poly ?? undefined })}
              selectedDistricts={selectedDistricts}
              onToggleDistrict={toggleDistrict}
              selectedSubdistricts={selectedSubdistricts}
              onToggleSubdistrict={toggleSubdistrict}
              requestedMode={requestedMapMode}
              onRequestedModeHandled={() => setRequestedMapMode(null)}
            />
          </Suspense>
          {/* Zero wyników NA mapie, nie zamiast mapy: podkład, „Rysuj", „Dzielnice" i +/−
              zostają pod spodem i dalej działają. `pointer-events-none` na warstwie, a
              `auto` na samej karcie — mapę da się przesuwać obok komunikatu. */}
          {!isLoading && pins.length === 0 && (
            // Karta stoi pośrodku PASA między dołem atrybucji a pigułką „Lista”, nie pośrodku mapy:
            // atrybucja kończy się 102 px od góry mapy (3,25 rem pod rzędem Rysuj/Dzielnice + 10 px
            // marginesu MapLibre + 40 px przycisku — od 29.09 cel dotyku 40, wcześniej 24 i karta
            // na środku się mieściła), pigułka zajmuje dolne 4 rem + strefę bezpieczną. Prawy zapas
            // 3,5 rem to kolumna legendy i +/−. Bramka: check-nachodzenie-mapy, stan w-zero-wynikow-mapa.
            <div
              className="pointer-events-none absolute inset-x-0 z-[1000] flex items-center pl-4 pr-14"
              style={{ top: 110, bottom: 'calc(4rem + env(safe-area-inset-bottom, 0px))' }}
            >
              <PustyWynik
                plywajacy
                naWyczysc={clearAll}
                naUsunObszar={maObszar ? usunObszary : undefined}
                poluzuj={poluzuj}
              />
            </div>
          )}
          </div>
        )}

        {posts.length > 0 && !isLoading && (isDesktop || view !== 'map') && (
          <>
            {/* Dimmed while a new filter is in flight: these are the PREVIOUS
                results (keepPreviousData), and showing them at full strength
                claims they answer the filter the user just set. Not
                pointer-events-none — a stale card is still a real offer, and
                blocking clicks to make a point would be hostile. */}
            {/* „Doszukuję oferty" — klik w pigułkę z dalekiej pozycji doładowuje strony i to
                trwa. Bez tej linijki lista stoi bez ruchu i wygląda, jakby kliknięcie nic nie
                zrobiło. */}
            {szukanaOferta && (
              <p role="status" className="pb-3 text-sm text-muted-foreground">
                {t('search.finding_offer')}
              </p>
            )}
            <div
              className={cn(
                'grid gap-x-4 gap-y-8 transition-opacity duration-200 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3',
                refreshing && 'opacity-40',
              )}
            >
              {posts.slice(0, visibleCount).map((p, i) => (
                <Fragment key={p.id}>
                {/* KRESKA FILTRÓW MIĘKKICH (SRCH-24 v2, Maciek): tu kończą się oferty potwierdzające
                    wszystkie wybrane kryteria miękkie. Bez liczb; każda karta pod kreską mówi sama,
                    czego w niej brakuje. */}
                {i === pozycjaKreskiListy && naglowekKreskiListy && (
                  <div className="col-span-full flex items-center gap-3 pt-2" data-kreska-miekkich>
                    <span className="h-px flex-1 bg-border" aria-hidden />
                    <p className="max-w-2xl text-center text-sm text-muted-foreground">{naglowekKreskiListy}</p>
                    <span className="h-px flex-1 bg-border" aria-hidden />
                  </div>
                )}
                {/* DRUGA KRESKA (Piotr 29.09): tu zaczynają się oferty bez zdjęć — wszystkie, bez
                    względu na dopasowanie. */}
                {i === pozycjaKreskiBezZdjecListy && (
                  <div className="col-span-full flex items-center gap-3 pt-2" data-kreska-bez-zdjec>
                    <span className="h-px flex-1 bg-border" aria-hidden />
                    <p className="max-w-2xl text-center text-sm text-muted-foreground">{t('search.kreska.bez_zdjec')}</p>
                    <span className="h-px flex-1 bg-border" aria-hidden />
                  </div>
                )}
                <SourcePostCard
                  post={p}
                  showPoiBadges={showPoiBadges}
                  onHover={handleCardHover}
                  podswietlona={podswietlonaKarta === p.id}
                  // Zdjęcia pierwszych kart ładujemy od razu, reszta leniwie — na wolnym
                  // łączu dwadzieścia zdjęć naraz konkurowało z zapytaniem o dane.
                  pilneZdjecie={i < PILNE_ZDJECIA}
                  zdjecieLcp={i === 0}
                  trybKosztu={filters.costMode === 'total'}
                  filtryMiekkie={filters}
                />
                </Fragment>
              ))}
            </div>
            {isFetchingNextPage && (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {t('search.loading_more')}
              </p>
            )}
            {(visibleCount < total || hasNextPage) && (
              <div ref={setSentinelEl} className="h-1" aria-hidden />
            )}
          </>
        )}
        </div>

        {/* RIGHT: sticky map (desktop hybrid). Mounted only at >= lg so Leaflet
            never initialises in a display:none / zero-size container. Sticks
            flush below the fixed nav (3.5rem) + the measured sticky-header height,
            so it never overlaps the header nor leaves a gap as the header grows. */}
        {isDesktop && (
          <div
            className="sticky hidden w-[42%] shrink-0 lg:block xl:w-[44%]"
            style={{
              top: `calc(var(--app-header-offset, var(--app-header-h, 3.5rem)) + ${headerH}px)`,
              height: `calc(100vh - var(--app-header-offset, var(--app-header-h, 3.5rem)) - ${headerH}px)`,
              transition: animujKolumneMapy ? 'top 200ms ease-out, height 200ms ease-out' : 'none',
            }}
          >
            <Suspense fallback={<div className="h-full w-full animate-pulse bg-muted/40" />}>
              <MapView
                pins={pins}
                bezAdresu={pastylkaBezAdresu}
                focusBounds={focusBounds}
                fill
                highlightedId={hoveredId}
                // SRCH-24 v2: klik w mapę otwiera kartę nad mapą — lista się nie przewija.
                sprzezenieZLista
                kamera={kameraStartowa}
                naKamere={naKamere}
                pomijajDopasowanie={!!kameraStartowa}
                boundaries={boundaries}
                stations={stations}
                lacznieOfert={wynikRazem}
                onNoty={setNotyMapy}
                przelacznikNaEkranie={!isDesktop}
                trybKosztu={filters.costMode === 'total'}
                filtryMiekkie={filters}
                pinyAktualne={!pinsQ.isPlaceholderData && (!pinsQ.isFetching || pinsQ.isFetchingNextPage)}
              pinyKompletne={pinsQ.data?.kompletne ?? false}
              przyciskiUstalone={przyciskiMapyUstalone}
                drawnPolygon={filters.polygon ?? null}
                onDrawnPolygonChange={(poly) => setFilters({ ...filters, polygon: poly ?? undefined })}
                selectedDistricts={selectedDistricts}
                onToggleDistrict={toggleDistrict}
                selectedSubdistricts={selectedSubdistricts}
                onToggleSubdistrict={toggleSubdistrict}
              />
            </Suspense>
          </div>
        )}
      </div>

      {/* Jednorazowe pytanie o e-maile po pierwszym zapisie (C3): okno na pulpicie, arkusz na telefonie. */}
      {mojeFiltry.widoczna && (
        <PytanieOMaile
          otwarte={mojeFiltry.pytanie}
          naTelefonie={!isDesktop}
          odpowiedz={mojeFiltry.odpowiedz}
          zamknij={mojeFiltry.zamknijPytanie}
        />
      )}
      <ToasterSonner
        id={TOASTER_WYSZUKIWARKI}
        // Pulpit: lewy dolny róg, nad listą (Piotr 28.09) — na środku toast zasłaniał kartę i
        // brzeg mapy. Telefon bez zmian.
        position={isDesktop ? 'bottom-left' : 'bottom-center'}
        mobileOffset={{ bottom: `calc(${ODSTEP_OD_DOLU} + 60px)` }}
        offset={{ bottom: 24 }}
        toastOptions={{ classNames: { actionButton: '!bg-primary !text-primary-foreground' } }}
      />
      {/* Mobile list/map toggle — a floating bottom-centre pill (airbnb/qasa) so
          it doesn't take a header row. Single button that flips in place: on the
          list it offers "Map", on the map it offers "List". Desktop hides it
          (hybrid shows both). */}
      <button
        type="button"
        data-plywajacy="przelacznik"
        onClick={() => setFilters({ ...filters, view: view === 'map' ? 'list' : 'map' })}
        // Zamiar wejścia na mapę → kawałki mapy od razu (patrz `dociagnijMape`).
        onPointerEnter={() => { if (view !== 'map') void dociagnijMape(); }}
        onFocus={() => { if (view !== 'map') void dociagnijMape(); }}
        onTouchStart={() => { if (view !== 'map') void dociagnijMape(); }}
        className="fixed left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-full bg-foreground px-5 py-3 text-sm font-semibold text-background shadow-lg transition-transform active:scale-95 lg:hidden"
        // Odstęp od dołu: własny margines plus pasek gestu, i nic więcej. Historia tej
        // jednej linijki (z tego, dlaczego NIE ma tu kompensacji paska przeglądarki)
        // stoi w src/lib/mobileBottomInset.ts — warto przeczytać przed zmianą.
        style={{ bottom: ODSTEP_OD_DOLU }}
        aria-label={view === 'map' ? t('search.view.list') : t('search.view.map')}
      >
        {view === 'map' ? (
          <>
            <List className="h-4 w-4" />
            {t('search.view.list')}
          </>
        ) : (
          <>
            <MapIcon className="h-4 w-4" />
            {t('search.view.map')}
          </>
        )}
      </button>
    </div>
  );
};

export default SearchPage;
